import ActivityRecruitingStage from "../components/ActivityRecruitingStage";
import { selectActivityDialogItems } from "../utils/activityDialogItems";
import { activityEntityType, activityEntityLabel, matchesActivityEntity, selectActivityTotals } from "../utils/activityEntityTotals";
import useActivityInfiniteScroll from "../hooks/useActivityInfiniteScroll";
import { fetchActivityWindow } from "../utils/activityPaging";
import ScheduledMeetingActions from "../components/ScheduledMeetingActions";
import CompanyTableCell from "../components/CompanyTableCell";
import { openInterviewScheduler, schedulingRequest, interviewsChanged } from "../utils/interviewScheduling";
import { activityBoardState, activityCompletionTime, mergeActivitySnapshot } from "../utils/activityBoardState";
import { activitySource } from "../utils/activitySource";
import { activityTypeCategory, activityPriorityCategory } from "../utils/activityFilterCategories";
import { activityDateRange, matchesActivityDate } from "../utils/activityDateFilter";
import ActivityCandidateText, { getActivityCandidates } from "../components/ActivityCandidateText";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import InterviewCalendar from "../components/InterviewCalendar";
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import ContentPasteSearchRoundedIcon from "@mui/icons-material/ContentPasteSearchRounded";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import PushPinRoundedIcon from "@mui/icons-material/PushPinRounded";
import { SearchRounded as SearchRoundedIcon } from "../components/icons/FigmaControlIcons";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import SentimentSatisfiedAltRoundedIcon from "@mui/icons-material/SentimentSatisfiedAltRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { readStoredUser } from "../utils/userSession";
import { LEAD_STATUS_VALUES } from "../utils/leadStatusOptions";
import {
  CANDIDATE_IMPORT_WORK_EVENT,
  clearCandidateImportWork,
  readCandidateImportWork,
} from "../utils/candidateImportWorkState";
import { fetchRecruiterUsers } from "../utils/teamMembers";
import { buildOpenCompanyNavPayload } from "../utils/companyNavigation";
import { createToolbarSelectSx } from "../components/toolbarControlStyles";
import { pillButtonInteractiveSx } from "../utils/pillButtonStyles";
import AIAgentDialog from "../components/PopupWindows/AIAgentDialog.jsx";
import Breadcrumb from "../components/Breadcrumb";
import GenericDataTable from "../components/GenericDataTable";
import ListTableTabsTemplate from "../components/layouts/ListTableTabsTemplate";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const BORDER = "#E8ECF7";
const TEXT = "#111827";
const MUTED = "#677489";
const SUBTLE = "#94A3B8";
const SURFACE = "#FFFFFF";
const PURPLE = "#5B4BFF";
const BLUE = "#377DFF";
const GREEN = "#16A34A";
const ORANGE = "#F59E0B";
const RED = "#EF4444";
const WORKSPACE_ACTIVITY_PAGE_SIZE = 60;

const activitiesFilterSelectSx = createToolbarSelectSx({
  textColor: TEXT,
  textFontSize: 13,
  outlineSx: { borderColor: BORDER },
});

const getStoredToken = () => {
  const direct =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("id_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("idToken") ||
    sessionStorage.getItem("id_token") ||
    "";
  if (String(direct || "").trim()) return String(direct).trim();

  for (const key of ["user", "auth", "session", "profile"]) {
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      const token =
        parsed?.token ||
        parsed?.accessToken ||
        parsed?.access_token ||
        parsed?.jwt ||
        parsed?.idToken ||
        parsed?.id_token ||
        "";
      if (String(token || "").trim()) return String(token).trim();
    } catch {
      // Ignore malformed cached authentication data.
    }
  }
  return "";
};

const getAuthHeaders = () => {
  const token = getStoredToken();
  if (!token) return {};
  const rawToken = token.replace(/^Bearer\s+/i, "");
  return {
    Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
    "x-access-token": rawToken,
  };
};

const getActivitiesApiUrls = ({ limit = WORKSPACE_ACTIVITY_PAGE_SIZE, offset = 0, includeTotals = true, dateStart, dateEnd } = {}) => {
  const base = String(API_BASE || "").replace(/\/$/, "");
  const apiRoot = base.replace(/\/api$/i, "");
  const query = new URLSearchParams({ scope: "workspace", limit: String(limit), offset: String(offset), includeTotals: includeTotals ? "1" : "0" });
  if (dateStart && dateEnd) { query.set("dateStart", dateStart); query.set("dateEnd", dateEnd); }
  return Array.from(
    new Set([
      `${apiRoot}/api/activities?${query}`,
      `${apiRoot}/activities?${query}`,
    ])
  );
};

const fetchWorkspaceActivityPage = async ({
  limit = WORKSPACE_ACTIVITY_PAGE_SIZE,
  offset = 0,
  includeTotals = true,
  dateStart, dateEnd, signal,
} = {}) => {
  let lastError = null;

  for (const url of getActivitiesApiUrls({ limit, offset, includeTotals, dateStart, dateEnd })) {
    try {
      const response = await fetch(url, {
        credentials: "include",
        headers: getAuthHeaders(),
        signal,
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) return { data, url };
      lastError = new Error(data?.error || `Unable to load activities (${response.status}).`);
    } catch (error) {
      if (signal?.aborted || error?.name === "AbortError") throw error;
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to reach the activities service.");
};

const fetchWorkspaceActivities = (options = {}) => fetchActivityWindow(fetchWorkspaceActivityPage, {
  limit: WORKSPACE_ACTIVITY_PAGE_SIZE, ...options,
});

const formatActivityDateTime = (value) => {
  const rawValue = String(value || "").trim();
  // MariaDB DATETIME values are UTC in this API but arrive without an offset.
  // Interpret those as UTC explicitly, then let Intl convert them to the
  // browser's timezone. Offset-aware / ISO values remain unchanged.
  const utcValue =
    rawValue &&
    !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(rawValue)
      ? `${rawValue.replace(" ", "T")}Z`
      : rawValue;
  const date = new Date(utcValue);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: browserTimeZone,
  }).format(date);
};

const getReviewSourceLabel = (metadata = {}, proposedRecord = {}) => {
  const explicitSource = [
    metadata.source,
    metadata.source_name,
    metadata.source_label,
    proposedRecord.source,
    proposedRecord.source_name,
    proposedRecord.site,
  ].find((value) => String(value || "").trim());
  if (explicitSource) return String(explicitSource).trim();

  switch (String(metadata.review_kind || "").toLowerCase()) {
    case "candidate_experience_company":
      return "Candidate resume";
    case "company_contact":
      return "Company team or leadership page";
    case "company_job":
    case "company_lead":
      return "Company careers site";
    default:
      return "AI discovery";
  }
};

const getPrimaryCompanyOptions = (item = {}) => {
  const values = Array.isArray(item?.metadata?.proposed_companies)
    ? item.metadata.proposed_companies
    : [];
  const seen = new Set();
  return values.filter((value) => {
    const name = String(value || "").trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getReviewJobLink = (metadata = {}) => {
  const proposal = metadata?.proposed_job || {};
  const value = [
    proposal.job_url,
    proposal.url,
    proposal.source_page_url,
  ].find((candidate) => String(candidate || "").trim());
  const url = String(value || "").trim();

  return /^https?:\/\//i.test(url) ? url : "";
};

const panelSx = {
  borderRadius: "22px",
  border: `1px solid ${BORDER}`,
  backgroundColor: SURFACE,
  boxShadow: "0 18px 48px rgba(15, 23, 42, 0.06)",
};

const filterConfigs = [
  {
    key: "status",
    label: "Status",
    value: "All Statuses",
    options: ["All Statuses", "Needs Review", "In Progress", "Completed", "Rejected", "Failed"],
  },
  {
    key: "source",
    label: "Source",
    value: "All Sources",
    options: ["All Sources", "Human", "Agent", "System"],
  },
  {
    key: "type",
    label: "Type",
    value: "All Activity Types",
    options: ["All Activity Types", "Candidate Match", "Recruiting Sequence", "Approval", "Scheduling", "Action", "Email", "Note"],
  },
  {
    key: "entity",
    label: "Entity",
    value: "All Entities",
    options: ["All Entities", "Candidates", "Jobs", "Companies", "Leads", "Contacts"],
  },
  {
    key: "priority",
    label: "Priority",
    value: "All Priorities",
    options: ["All Priorities", "Critical", "High", "Medium", "Low", "Unspecified"],
  },
  {
    key: "date",
    label: "Date",
    value: "This Week",
    options: ["Today", "This Week", "Last 7 Days", "This Month", "All Time"],
  },
];

const metricCards = [
  {
    title: "Needs Review",
    value: "38",
    detail: "12% vs yesterday",
    tone: "success",
    accent: "#8B5CF6",
    soft: "#F3E8FF",
    icon: <ContentPasteSearchRoundedIcon sx={{ fontSize: 20 }} />,
  },
  {
    title: "In Progress",
    value: "108",
    detail: "8% vs yesterday",
    tone: "success",
    accent: "#3B82F6",
    soft: "#EAF2FF",
    icon: <AutorenewRoundedIcon sx={{ fontSize: 20 }} />,
  },
  {
    title: "Completed",
    value: "10,421",
    detail: "24% vs yesterday",
    tone: "success",
    accent: "#16A34A",
    soft: "#EAF8EE",
    icon: <TaskAltRoundedIcon sx={{ fontSize: 20 }} />,
  },
  {
    title: "Failed",
    value: "14",
    detail: "3% vs yesterday",
    tone: "danger",
    accent: "#EF4444",
    soft: "#FEECEC",
    icon: <HighlightOffRoundedIcon sx={{ fontSize: 20 }} />,
  },
  {
    title: "Active Agents",
    value: "8",
    detail: "3 running",
    tone: "neutral",
    accent: "#2563EB",
    soft: "#ECF3FF",
    icon: <PrecisionManufacturingRoundedIcon sx={{ fontSize: 20 }} />,
  },
  {
    title: "Open Tasks",
    value: "52",
    detail: "6 vs yesterday",
    tone: "success",
    accent: "#F59E0B",
    soft: "#FFF5E6",
    icon: <ChecklistRoundedIcon sx={{ fontSize: 20 }} />,
  },
  {
    title: "My Activities",
    value: "84",
    detail: "18 vs yesterday",
    tone: "success",
    accent: "#7C3AED",
    soft: "#F3E8FF",
    icon: <PersonOutlineRoundedIcon sx={{ fontSize: 20 }} />,
  },
];

const chatThreads = [
  {
    section: "Pinned",
    title: "Lead Agent",
    subtitle: "Python LA Search",
    detail: "Focused shortlist ready for review",
    time: "9:41 AM",
    accent: "#8B5CF6",
    kind: "agent",
    pinned: true,
  },
  {
    section: "Pinned",
    title: "JP Panda",
    subtitle: "Rameet: please review",
    detail: "Candidate approval requested",
    time: "9:12 AM",
    accent: "#F59E0B",
    kind: "human",
    pinned: true,
  },
  {
    section: "Pinned",
    title: "# Recruiting",
    subtitle: "Kristen: proposal approved",
    detail: "Client proposal approved",
    time: "8:30 AM",
    accent: "#3B82F6",
    kind: "channel",
  },
  {
    section: "Today",
    title: "Candidate Agent",
    subtitle: "Talkspace AI Engineer",
    detail: "Agent is screening candidates",
    time: "10:15 AM",
    accent: "#2563EB",
    kind: "agent",
  },
  {
    section: "Today",
    title: "Vishal Shettiger",
    subtitle: "Shared a candidate",
    detail: "Forwarded profile for review",
    time: "9:02 AM",
    accent: "#0EA5E9",
    kind: "human",
  },
  {
    section: "Today",
    title: "# Sales",
    subtitle: "JP: agreement signed",
    detail: "Commercial workflow updated",
    time: "8:45 AM",
    accent: "#A855F7",
    kind: "channel",
  },
  {
    section: "Yesterday",
    title: "Content Agent",
    subtitle: "Sequence Approval",
    detail: "Approved content request",
    time: "Yesterday",
    accent: "#6366F1",
    kind: "agent",
  },
  {
    section: "Yesterday",
    title: "Rupal Patel",
    subtitle: "Thanks!",
    detail: "Candidate follow-up complete",
    time: "Yesterday",
    accent: "#22C55E",
    kind: "human",
  },
  {
    section: "Last 7 Days",
    title: "Job Agent",
    subtitle: "New Jobs - Fintech",
    detail: "28 jobs sourced from Series A-C companies",
    time: "2d ago",
    accent: "#2563EB",
    kind: "agent",
  },
  {
    section: "Last 7 Days",
    title: "Kristen Frost",
    subtitle: "Call notes",
    detail: "Shared after discovery call",
    time: "3d ago",
    accent: "#F97316",
    kind: "human",
  },
];

const agendaItems = [
  {
    time: "9:00 AM",
    title: "Interview - John Smith",
    subtitle: "Sr AI Engineer - Talkspace",
  },
  {
    time: "11:00 AM",
    title: "Discovery Call",
    subtitle: "Talkspace",
  },
  {
    time: "1:00 PM",
    title: "Candidate Review",
    subtitle: "Python Engineers LA Search",
  },
  {
    time: "3:00 PM",
    title: "Proposal Follow Up",
    subtitle: "CloudBees",
  },
];

const conversationMessages = [
  {
    author: "Lead Agent",
    role: "assistant",
    time: "9:41 AM",
    text: "I found 42 Python jobs in Los Angeles.",
  },
  {
    author: "Rameet Singh",
    role: "user",
    time: "9:42 AM",
    text: "Show only Series A-C companies.",
  },
  {
    author: "Lead Agent",
    role: "assistant",
    time: "9:42 AM",
    text: "Got it. Filtering results now...",
  },
  {
    author: "Lead Agent",
    role: "assistant",
    time: "9:43 AM",
    text: "Here are 28 jobs from Series A-C",
    cta: "View Results (28)",
  },
];

const TEAM_MEMBER_ACCENTS = ["#8B5CF6", "#2563EB", "#F97316", "#0EA5E9", "#16A34A", "#DB2777"];

const getInitials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "U";

function normalizePlatformTeamMembers(users, currentUser) {
  const membersById = new Map();
  const add = (user) => {
    const id = String(user?.id ?? user?.user_id ?? "").trim();
    const name = String(user?.name || user?.full_name || "").trim();
    if (!id || !name) return;
    membersById.set(id, {
      id,
      name,
      avatarUrl: String(user?.avatar_url || user?.profile_image || user?.avatar || "").trim(),
    });
  };
  (Array.isArray(users) ? users : []).forEach(add);
  add(currentUser);
  return Array.from(membersById.values())
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((member, index) => ({
      ...member,
      initials: getInitials(member.name),
      active: true,
      accent: TEAM_MEMBER_ACCENTS[index % TEAM_MEMBER_ACCENTS.length],
    }));
}

const agentItems = [
  { name: "Lead Agent", status: "Running", tone: "success" },
  { name: "Candidate Agent", status: "Running", tone: "success" },
  { name: "Company Agent", status: "Idle", tone: "neutral" },
  { name: "Content Agent", status: "Paused", tone: "warning" },
  { name: "Job Agent", status: "Failed", tone: "danger" },
];

const columnGroups = [
  {
    key: "review",
    label: "Needs Review",
    count: "38",
    accent: "#5B4BFF",
    cards: [
      {
        title: "Candidate Match",
        subtitle: "AI matched John Smith",
        detail: "Sr AI Engineer",
        meta: ["Candidate: John Smith", "Job: Sr AI Engineer"],
        owner: "Agent",
        ownerName: "Candidate Agent",
        due: "Today 4:00 PM",
        priority: { label: "High", tone: "danger" },
        actions: ["View", "Approve", "Reject"],
      },
      {
        title: "Content Approval",
        subtitle: "Review Sequence",
        detail: "Job: Staff Engineer",
        meta: ["Owner: Vishal Shettiger", "Human: Vishal"],
        owner: "Owner",
        ownerName: "Vishal Shettiger",
        due: "Tomorrow 11:00 AM",
        priority: { label: "Medium", tone: "warning" },
        actions: ["View", "Approve", "Reject"],
      },
      {
        title: "New Job Added",
        subtitle: "Assign Recruiting Team",
        detail: "Job: AI Engineer",
        meta: ["Owner: Rameet Singh", "Human: Rameet"],
        owner: "Owner",
        ownerName: "Rameet Singh",
        due: "Tomorrow 9:00 AM",
        priority: { label: "High", tone: "danger" },
        actions: ["View", "Assign", "Dismiss"],
      },
    ],
  },
  {
    key: "progress",
    label: "In Progress",
    count: "108",
    accent: "#377DFF",
    cards: [
      {
        title: "Candidate Agent",
        subtitle: "Python Engineers LA Search",
        detail: "72 / 100 Candidates Contacted",
        progress: 72,
        meta: ["Agent", "Candidate Agent"],
        footer: ["Started: 2 hrs ago", "ETA: 45 mins"],
        status: { label: "Running", tone: "success" },
        actions: ["View Progress", "Open Chat", "Pause"],
      },
      {
        title: "Recruiting Sequence",
        subtitle: "Outreach Campaign - Talkspace",
        detail: "91 / 100 Emails Sent",
        progress: 91,
        meta: ["Human", "Kristen Frost"],
        footer: ["Started: 1 hr ago", "ETA: 5 mins"],
        status: { label: "Running", tone: "success" },
        actions: ["View Progress", "Open Chat", "Pause"],
      },
      {
        title: "Interview Scheduling",
        subtitle: "John Smith - Sr AI Engineer",
        detail: "Scheduling in progress with hiring team",
        progress: 48,
        meta: ["Human", "Kristen Frost"],
        footer: ["Started: 30 mins ago"],
        status: { label: "In Progress", tone: "info" },
        actions: ["View", "Open Chat", "Cancel"],
      },
    ],
  },
  {
    key: "complete",
    label: "Completed",
    count: "10,421",
    accent: "#16A34A",
    cards: [
      {
        title: "Candidate Hired",
        subtitle: "John Smith",
        detail: "Position: Sr AI Engineer",
        meta: ["Company: Talkspace", "Human: Rameet Singh"],
        completedAt: "Completed 10 mins ago",
        priority: { label: "Low", tone: "success" },
        actions: ["View", "Duplicate", "Reopen"],
      },
      {
        title: "Candidate Submitted",
        subtitle: "Jane Doe",
        detail: "Position: Product Manager",
        meta: ["Company: Airbnb", "Human: JP Panda"],
        completedAt: "Completed 25 mins ago",
        priority: { label: "Low", tone: "success" },
        actions: ["View", "Duplicate", "Reopen"],
      },
      {
        title: "Lead Added",
        subtitle: "42 Leads Added",
        detail: "Target: Talkspace",
        meta: ["Agent: Lead Agent", "Completed 1 hr ago"],
        priority: { label: "Low", tone: "success" },
        actions: ["View", "Open Leads", "Duplicate"],
      },
    ],
  },
];

function toneColor(tone = "neutral") {
  if (tone === "success") return GREEN;
  if (tone === "warning") return ORANGE;
  if (tone === "danger") return RED;
  if (tone === "info") return BLUE;
  return MUTED;
}

function toneSoft(tone = "neutral") {
  if (tone === "success") return alpha(GREEN, 0.1);
  if (tone === "warning") return alpha(ORANGE, 0.12);
  if (tone === "danger") return alpha(RED, 0.1);
  if (tone === "info") return alpha(BLUE, 0.1);
  return "#F3F4F6";
}

function StatCard({ item }) {
  return (
    <Box
      sx={{
        ...panelSx,
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.6,
        minHeight: 98,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "16px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: item.accent,
          backgroundColor: item.soft,
          boxShadow: `inset 0 0 0 1px ${alpha(item.accent, 0.12)}`,
          flexShrink: 0,
        }}
      >
        {item.icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, color: MUTED, mb: 0.35 }}>{item.title}</Typography>
        <Typography sx={{ fontSize: 15, color: toneColor(item.tone), lineHeight: 1.45 }}>
          {item.tone === "danger" ? "-" : item.tone === "neutral" ? "" : "+"} {item.detail}
        </Typography>
        <Typography sx={{ fontSize: 34, fontWeight: 700, color: TEXT, lineHeight: 1 }}>
          {item.value}
        </Typography>
      </Box>
    </Box>
  );
}

function InsightCard() {
  return (
    <Box
      sx={{
        ...panelSx,
        p: 2,
        minHeight: 98,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 120 52"
        sx={{ width: "100%", height: 54 }}
      >
        <defs>
          <linearGradient id="activity-insight-line" x1="0%" x2="100%" y1="0%" y2="0%">
            <stop offset="0%" stopColor="#5B4BFF" />
            <stop offset="100%" stopColor="#377DFF" />
          </linearGradient>
        </defs>
        <polyline
          fill="none"
          stroke="url(#activity-insight-line)"
          strokeWidth="3"
          points="8,34 28,28 48,36 68,18 88,26 108,12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {[8, 28, 48, 68, 88, 108].map((x, index) => {
          const y = [34, 28, 36, 18, 26, 12][index];
          return <circle key={x} cx={x} cy={y} r="3.8" fill="#FFFFFF" stroke="#5B4BFF" strokeWidth="2" />;
        })}
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: TEXT }}>
        More Insights
      </Typography>
    </Box>
  );
}

function ActionButton({ label, icon, primary = false, onClick, disabled = false, color = TEXT }) {
  return (
    <Button
      startIcon={icon}
      onClick={onClick}
      disabled={disabled}
      sx={{
        borderRadius: "14px",
        height: 42,
        px: 2,
        textTransform: "none",
        fontWeight: 600,
        fontSize: 13,
        color: primary ? "#FFFFFF" : color,
        background: primary
          ? "linear-gradient(135deg, #6B5BFF 0%, #4F46E5 100%)"
          : "#FFFFFF",
        border: primary ? "none" : `1px solid ${BORDER}`,
        boxShadow: primary ? "0 16px 32px rgba(91, 75, 255, 0.2)" : "none",
        "&:hover": {
          background: primary
            ? "linear-gradient(135deg, #5C4EF7 0%, #4338CA 100%)"
            : "#F8FAFC",
        },
      }}
    >
      {label}
    </Button>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0.55,
      }}
    >
      <Typography sx={{ fontSize: 11, color: MUTED, fontWeight: 500 }}>
        {label}
      </Typography>
      <Select
        size="small"
        inputProps={{ "aria-label": label }}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        IconComponent={KeyboardArrowDownRoundedIcon}
        sx={{
          minWidth: 0,
          ...activitiesFilterSelectSx,
        }}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option} sx={{ fontWeight: 500 }}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function ChatThreadRow({ item }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "36px minmax(0, 1fr) auto",
        alignItems: "start",
        gap: 1.1,
        py: 1.2,
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: alpha(item.accent, 0.16),
          color: item.accent,
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {item.kind === "agent" ? "AI" : item.title.charAt(0)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT }}>
          {item.title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: TEXT, opacity: 0.8 }}>
          {item.subtitle}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.3 }}>
          {item.detail}
        </Typography>
      </Box>
      <Stack alignItems="flex-end" spacing={0.5}>
        <Typography sx={{ fontSize: 11, color: MUTED }}>{item.time}</Typography>
        {item.pinned ? <PushPinRoundedIcon sx={{ fontSize: 16, color: "#1F3A8A" }} /> : null}
      </Stack>
    </Box>
  );
}

function TeamAvatar({ item, selected = false, onClick }) {
  return (
    <Stack
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      spacing={0.75}
      alignItems="center"
      sx={{
        minWidth: 54,
        cursor: "pointer",
        border: 0,
        p: 0.45,
        borderRadius: 2,
        background: selected ? alpha(PURPLE, 0.1) : "transparent",
        boxShadow: selected ? `0 0 0 1px ${PURPLE}` : "none",
        "&:hover": { background: alpha(PURPLE, 0.07) },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Avatar
          src={item.avatarUrl || undefined}
          sx={{
            width: 38,
            height: 38,
            bgcolor: alpha(item.accent, 0.14),
            color: item.accent,
            fontSize: 13,
            fontWeight: 700,
            boxShadow: `0 0 0 2px ${SURFACE}`,
          }}
        >
          {!item.avatarUrl ? item.initials : null}
        </Avatar>
        <Box
          sx={{
            position: "absolute",
            right: -1,
            bottom: -1,
            width: 11,
            height: 11,
            borderRadius: "50%",
            bgcolor: item.active ? "#22C55E" : "#CBD5E1",
            boxShadow: "0 0 0 2px #FFFFFF",
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 12, color: TEXT, textAlign: "center" }}>
        {item.name}
      </Typography>
    </Stack>
  );
}

function AgentPill({ item }) {
  return (
    <Box
      sx={{
        px: 1.25,
        py: 1,
        borderRadius: "14px",
        border: `1px solid ${BORDER}`,
        backgroundColor: "#FFFFFF",
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT }}>
        {item.name}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.45 }}>
        <CircleRoundedIcon sx={{ fontSize: 10, color: toneColor(item.tone) }} />
        <Typography sx={{ fontSize: 11, color: MUTED }}>
          {item.status}
        </Typography>
      </Stack>
    </Box>
  );
}

function StatusChip({ item }) {
  if (!item) return null;

  return (
    <Chip
      size="small"
      label={item.label}
      sx={{
        height: 24,
        borderRadius: "999px",
        bgcolor: toneSoft(item.tone),
        color: toneColor(item.tone),
        fontWeight: 700,
        "& .MuiChip-label": { px: 1.1 },
      }}
    />
  );
}

function ActivityEntityIcon({ item, sx }) {
  const reviewKind = String(item?.metadata?.review_kind || "").toLowerCase();
  const entityType = String(item?.entityType || "").toLowerCase();

  if (reviewKind === "company_contact" || entityType === "contact") {
    return <PersonOutlineRoundedIcon sx={sx} />;
  }
  if (reviewKind === "company_lead" || entityType === "lead") {
    return <PersonSearchOutlinedIcon sx={sx} />;
  }
  if (reviewKind === "company_job" || entityType === "job") {
    return <WorkOutlineRoundedIcon sx={sx} />;
  }
  if (entityType === "company") {
    return <BusinessOutlinedIcon sx={sx} />;
  }
  return <AutoAwesomeRoundedIcon sx={sx} />;
}

function ActivityCard({ item, accent, onAction, onReject, onView, onViewAll, onStop, onOpenCompany, onOpenCandidate }) {
  const isWorking = item?.state === "progress";
  const reviewJobLink = item?.state === "review" ? getReviewJobLink(item.metadata) : "";
  const canOpenCompletedCompany = item?.state === "complete" && Number(item?.company?.id) > 0;
  return (
    <Box
      sx={{
        borderRadius: "18px",
        border: `1px solid ${alpha(accent, 0.14)}`,
        backgroundColor: "#FFFFFF",
        p: 1.6,
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="flex-start">
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "10px",
            backgroundColor: alpha(accent, 0.1),
            color: accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ActivityEntityIcon item={item} sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography
              component={reviewJobLink ? "a" : canOpenCompletedCompany ? "button" : "span"}
              href={reviewJobLink || undefined}
              target={reviewJobLink ? "_blank" : undefined}
              rel={reviewJobLink ? "noopener noreferrer" : undefined}
              type={canOpenCompletedCompany ? "button" : undefined}
              onClick={canOpenCompletedCompany ? () => onOpenCompany?.(item.company) : undefined}
              sx={{
                p: 0,
                border: 0,
                background: "transparent",
                textAlign: "left",
                cursor: reviewJobLink || canOpenCompletedCompany ? "pointer" : "default",
                font: "inherit",
                fontSize: 14.5,
                fontWeight: 700,
                color: reviewJobLink || canOpenCompletedCompany ? BLUE : TEXT,
                textDecoration: "none",
                "&:hover": reviewJobLink || canOpenCompletedCompany ? { textDecoration: "underline" } : undefined,
              }}
            >
              {item.title}
            </Typography>
            <StatusChip item={item.priority || item.status} />
          </Stack>
          <Typography
            component="span"
            sx={{
              display: "block",
              p: 0,
              border: 0,
              background: "transparent",
              textAlign: "left",
              font: "inherit",
              fontSize: 13,
              color: TEXT,
              mt: 0.25,
            }}
          >
            <ActivityCandidateText text={item.subtitle} candidates={item.candidates} />
          </Typography>
          {item.companyLink ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.35 }}>
              Company: {item.companyLink.existingId ? (
                <Box component="button" type="button" onClick={() => onOpenCompany?.(item.company)} sx={{ p: 0, border: 0, bgcolor: "transparent", color: BLUE, cursor: "pointer", font: "inherit", "&:hover": { textDecoration: "underline" } }}>
                  {item.companyLink.name}
                </Box>
              ) : item.companyLink.website ? (
                <Box component="a" href={item.companyLink.website} target="_blank" rel="noreferrer" sx={{ color: BLUE, "&:hover": { textDecoration: "underline" } }}>
                  {item.companyLink.name}
                </Box>
              ) : item.companyLink.name}
              {item.companyLink.existingId ? " · Already exists" : ""}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.35 }}>
              {item.detail}
            </Typography>
          )}

          {item.contactTitle !== undefined ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.35, overflowWrap: "anywhere" }}>
              Title: {item.contactTitle || "Not provided"}
            </Typography>
          ) : null}

          {item.completionDetails ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.65, overflowWrap: "anywhere" }}>
              <ActivityCandidateText text={item.completionDetails} candidates={item.candidates} />
            </Typography>
          ) : null}

          {item.progress ? (
            <Box sx={{ mt: 1.2 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.6 }}>
                <Stack direction="row" spacing={0.65} alignItems="center">
                  {isWorking ? (
                    <AutorenewRoundedIcon
                      sx={{
                        fontSize: 14,
                        color: BLUE,
                        animation: "activity-working-spin 1.1s linear infinite",
                        "@keyframes activity-working-spin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  ) : null}
                  <Typography sx={{ fontSize: 12, color: MUTED }}>
                    {isWorking ? "Working now" : item.detail}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT }}>
                  {item.progress}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={item.progress}
                sx={{
                  height: 7,
                  borderRadius: "999px",
                  backgroundColor: "#E7EDF8",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: "999px",
                    background: "linear-gradient(90deg, #5B4BFF 0%, #377DFF 100%)",
                  },
                }}
              />
            </Box>
          ) : null}

          <Stack spacing={0.45} sx={{ mt: 1.15 }}>
            {(item.reviewContext || item.meta || []).map((entry) => (
              <Typography key={entry} sx={{ fontSize: 12, color: "#475569" }}>
                {entry}
              </Typography>
            ))}
          </Stack>

          {item.footer ? (
            <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 1.05 }}>
              {item.footer.map((entry) => (
                <Typography key={entry} sx={{ fontSize: 12, color: MUTED }}>
                  {entry}
                </Typography>
              ))}
            </Stack>
          ) : null}

          {item.due ? (
            <Typography sx={{ fontSize: 12.5, color: "#F97316", fontWeight: 600, mt: 1.05 }}>
              Due: {item.due}
            </Typography>
          ) : null}

          {item.completedAt ? (
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 1.05 }}>
              {item.completedAt}
            </Typography>
          ) : null}
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.45 }}>
        <Stack direction="row" spacing={0.9} flexWrap="wrap" sx={{ flex: 1 }}>
          <ActivityRecruitingStage item={item} />
          {item.metadata?.calendar_event_id && <ScheduledMeetingActions record={{ ...item.metadata, id: item.id, can_manage: item.canManageMeeting }} />}
          {item.actions.map((action, index) => {
            const isPrimary = index === 1 && ["Approve", "Open Chat"].includes(action);
            const isReject = action === "Reject";
            const isStop = action === "Stop";
            const actionColor = isPrimary ? PURPLE : isStop ? RED : TEXT;
            const button = (
              <Button
                key={action}
                size="small"
                variant={isPrimary ? "contained" : "outlined"}
                className={isPrimary ? "pill" : undefined}
                onClick={
                  action === "Schedule" || action === "Interviews"
                    ? () => openInterviewScheduler({ candidateId: item.metadata?.candidate_id || item.entityId, jobId: item.metadata?.job_id,
                        stage: item.metadata?.next_stage || item.metadata?.stage, scheduleTaskId: action === "Schedule" ? item.id : undefined })
                    : action === "Dismiss"
                    ? () => onAction?.(item, "dismiss")
                    : action === "View"
                    ? () => onView?.(item)
                    : action === "Stop"
                    ? () => onStop?.(item)
                    : action === "Reject"
                    ? () => onReject?.(item)
                    : action === "Approve"
                      ? () => onAction?.(item, "approve")
                      : undefined
                }
                sx={{
                  minWidth: 0,
                  px: 1.3,
                  py: 0.8,
                  borderRadius: isReject ? "10px" : "999px",
                  textTransform: "none",
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: isPrimary ? "#FFFFFF" : actionColor,
                  background: isPrimary ? `${PURPLE} !important` : "#FFFFFF",
                  border: `1px solid ${isPrimary ? actionColor : BORDER}`,
                  "&:hover": {
                    background: `${isReject ? "#F8FAFC" : "#FFFFFF"} !important`,
                    color: isPrimary ? `${actionColor} !important` : actionColor,
                    borderColor: isPrimary ? `${actionColor} !important` : BORDER,
                  },
                }}
              >
                {action}
              </Button>
            );
            if (action === "View" && item.reviewEntityType) {
              const typeLabel = item.reviewEntityType === "company" ? "companies" : `${item.reviewEntityType}s`;
              return <React.Fragment key={action}>{button}<Button size="small" onClick={() => onViewAll?.(item.reviewEntityType, item.state)} sx={{ minWidth: 0, px: 1.3, py: 0.8, borderRadius: "999px", textTransform: "none", fontSize: 11.5, fontWeight: 600, color: BLUE, border: `1px solid ${BORDER}`, backgroundColor: "#FFFFFF", "&:hover": { backgroundColor: "#F8FAFC", borderColor: BLUE } }}>View all {typeLabel}</Button></React.Fragment>;
            }
            return button;
          })}
        </Stack>
        <IconButton sx={{ width: 30, height: 30, color: MUTED }}>
          <MoreHorizRoundedIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}

function ColumnGroup({
  item,
  onAction,
  onReject,
  onView,
  onViewAll,
  onStop,
  onOpenCompany,
  onOpenCandidate,
  onLoadMore,
  hasMore,
  loadingMore,
  loadError,
}) {
  const cards = Array.isArray(item?.cards) ? item.cards : [];
  const [visibleCardCount, setVisibleCardCount] = useState(12);
  const previousCardsLengthRef = useRef(cards.length);
  const scrollContainerRef = useRef(null);
  const loadSentinelRef = useRef(null);
  const canFetchMore = hasMore && Number(item.count) > cards.length && !loadError;
  useEffect(() => {
    const previousLength = previousCardsLengthRef.current;
    setVisibleCardCount((current) => {
      // A server page may arrive just before the final local cards are shown.
      // Reveal part of it immediately so the sentinel moves below the current
      // viewport instead of repeatedly requesting more pages in place.
      if (previousLength > 0 && cards.length > previousLength && current >= previousLength - 12) {
        return Math.min(cards.length, current + 12);
      }
      return Math.min(Math.max(12, current), cards.length);
    });
    previousCardsLengthRef.current = cards.length;
  }, [cards.length]);

  const revealNextCards = useCallback(() => {
    const nextVisibleCardCount = Math.min(cards.length, visibleCardCount + 12);
    if (visibleCardCount < cards.length) {
      setVisibleCardCount(nextVisibleCardCount);
    }
    // Only ask the server for another page once almost all locally fetched
    // cards have been revealed. This is what prevents a visible sentinel from
    // looping through every page while the user is still reading this one.
    if (canFetchMore && !loadingMore && nextVisibleCardCount >= cards.length - 12) onLoadMore?.();
  }, [cards.length, onLoadMore, visibleCardCount, canFetchMore, loadingMore]);

  useActivityInfiniteScroll({ rootRef: scrollContainerRef, sentinelRef: loadSentinelRef,
    enabled: visibleCardCount < cards.length || canFetchMore, loading: loadingMore, onLoadMore: revealNextCards });

  const visibleCards = cards.slice(0, visibleCardCount);
  const handleScroll = useCallback(
    (event) => {
      const target = event.currentTarget;
      // Request the next server page well before the viewer reaches the end
      // of a column. The previous 96px threshold made a completed column look
      // stalled until the scrollbar was already at its absolute bottom.
      if (target.scrollTop + target.clientHeight < target.scrollHeight - 360) return;
      revealNextCards();
    },
    [revealNextCards]
  );
  const reviewEntityTypes = item?.key === "review"
    ? cards.map((card) => card?.reviewEntityType).filter(Boolean)
    : [];
  // Career-site openings are Leads. Prefer the Leads destination whenever
  // that review column contains any of those opening proposals.
  const viewAllType = reviewEntityTypes.includes("lead")
    ? "lead"
    : reviewEntityTypes[0] || "";
  const viewAllLabel = viewAllType === "company" ? "View all companies" : `View all ${viewAllType}s`;

  return (
    <Box
      sx={{
        ...panelSx,
        height: 670,
        p: 1.6,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.35 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: item.accent, textTransform: "uppercase" }}>
          {item.label}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {viewAllType ? (
            <Button
              size="small"
              onClick={() => onViewAll?.(viewAllType, item.key)}
              sx={{
                minWidth: 0,
                p: 0,
                textTransform: "none",
                fontSize: 12,
                fontWeight: 700,
                color: BLUE,
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              }}
            >
              {viewAllLabel}
            </Button>
          ) : null}
          <Typography sx={{ fontSize: 14, color: MUTED }}>{item.count}</Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={1.35}
        onScroll={handleScroll}
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pr: 0.6,
          scrollbarGutter: "stable",
          "& > *": { flexShrink: 0 },
          "&::-webkit-scrollbar": { width: 7 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E1", borderRadius: 999 },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        }}
      >
        {visibleCards.map((card, index) => (
          <ActivityCard
            key={`${item.key}-${card.id || `${card.title}-${index}`}`}
            item={card}
            accent={item.accent}
            onAction={item.key === "review" ? onAction : undefined}
            onReject={item.key === "review" ? onReject : undefined}
            // Keep View actions available in every column. The parent binds
            // View all to this column so the dialog preserves its status.
            onView={onView}
            onViewAll={(type) => onViewAll?.(type, item.key)}
            onStop={item.key === "progress" ? onStop : undefined}
            onOpenCompany={onOpenCompany}
            onOpenCandidate={onOpenCandidate}
          />
        ))}
        {visibleCardCount < cards.length || canFetchMore ? (
          <Box
            ref={loadSentinelRef}
            aria-live="polite"
            sx={{ pt: 0.25, pb: 0.5, minHeight: 28 }}
          >
            {loadingMore ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 0.5,
                }}
              >
                <CircularProgress aria-label="Loading more activities" size={18} thickness={4.5} sx={{ color: "#9747FF" }} />
                {/*
                  Loading more activities…
                */}
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}

function LegendItem({ color, label, description }) {
  return (
    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
      <CircleRoundedIcon sx={{ fontSize: 10, color }} />
      <Typography sx={{ fontSize: 12, color: MUTED }}>
        <Box component="span" sx={{ color: TEXT, fontWeight: 600 }}>
          {label}
        </Box>{" "}
        {description}
      </Typography>
    </Stack>
  );
}

function Activities() {
  const navigate = useNavigate();
  const storedUser = readStoredUser();
  const currentUserName = storedUser?.name || storedUser?.full_name || "Rameet Singh";
  const [chatSearch, setChatSearch] = useState("");
  const [assistantTab, setAssistantTab] = useState("chat");
  const [filterValues, setFilterValues] = useState(() =>
    filterConfigs.reduce((acc, item) => ({ ...acc, [item.key]: item.value }), {})
  );
  const dateRange = useMemo(() => activityDateRange(filterValues.date), [filterValues.date]);
  const [platformTeamUsers, setPlatformTeamUsers] = useState([]);
  const [selectedTeamMemberIds, setSelectedTeamMemberIds] = useState([]);
  const [reviewItemPendingRejection, setReviewItemPendingRejection] = useState(null);
  const [primaryCompanySelection, setPrimaryCompanySelection] = useState(null);
  const [selectedPrimaryCompanyName, setSelectedPrimaryCompanyName] = useState("");
  const [reviewItemViewingDetails, setReviewItemViewingDetails] = useState(null);
  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const reviewItemsDialogType = itemsDialogOpen ? activityEntityType(filterValues.entity) || "all" : "";
  const setReviewItemsDialogType = useCallback((type) => {
    setItemsDialogOpen(Boolean(type));
    if (type) setFilterValues((current) => ({ ...current, entity: activityEntityLabel(type) }));
  }, []);
  const [itemsDialogGroup, setItemsDialogGroup] = useState("review");
  const isReviewItemsDialog = itemsDialogGroup === "review";
  const [selectedReviewItemIds, setSelectedReviewItemIds] = useState([]);
  useEffect(() => {
    setSelectedReviewItemIds([]);
  }, [filterValues.entity]);
  const [addingReviewItemsToAgent, setAddingReviewItemsToAgent] = useState(false);
  const [bulkRejectingReviewItems, setBulkRejectingReviewItems] = useState(false);
  const [workspaceActivities, setWorkspaceActivities] = useState([]);
  const [companyDirectoryByName, setCompanyDirectoryByName] = useState(() => new Map());
  const [candidateReviewCompanyWebsites, setCandidateReviewCompanyWebsites] = useState(() => new Map());
  const [candidateImportWork, setCandidateImportWork] = useState(() => readCandidateImportWork());
  const [workspaceActivitiesError, setWorkspaceActivitiesError] = useState("");
  const [workspaceActivitiesRefreshing, setWorkspaceActivitiesRefreshing] = useState(false);
  const [workspaceActivitiesLoadingMore, setWorkspaceActivitiesLoadingMore] = useState(false);
  const [workspaceActivitiesHasMore, setWorkspaceActivitiesHasMore] = useState(true);
  const [workspaceActivityTotals, setWorkspaceActivityTotals] = useState(null);
  const [workspaceActivitiesUpdatedAt, setWorkspaceActivitiesUpdatedAt] = useState(null);
  const [pendingCompanyEnrichment, setPendingCompanyEnrichment] = useState(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem("rpo:pending-company-enrichment") || "null");
      return stored?.name && Date.now() - Number(stored.startedAt || 0) < 5 * 60 * 1000 ? stored : null;
    } catch {
      return null;
    }
  });

  const [approvingActivityIds, setApprovingActivityIds] = useState(() => new Set());
  const [stoppingActivityIds, setStoppingActivityIds] = useState(() => new Set());
  const [deletingAllActivities, setDeletingAllActivities] = useState(false);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [agentPrompt, setAgentPrompt] = useState("");
  const [newActivityOpen, setNewActivityOpen] = useState(false);
  const [newActivitySaving, setNewActivitySaving] = useState(false);
  const [newActivityForm, setNewActivityForm] = useState({ entityType: "company", entityId: "", title: "", details: "" });
  const seenApprovedProposalIdsRef = useRef(null);
  const workspaceActivitiesLoadingMoreRef = useRef(false);
  const workspaceActivitiesExhaustedRef = useRef(false);
  const workspaceActivitiesCountRef = useRef(0);
  // A GET started just before Delete all can return the old board after the
  // DELETE succeeds. Keep a generation counter so that stale response can
  // never repopulate Needs Review (or any other) cards.
  const workspaceActivitiesGenerationRef = useRef(0);
  const workspaceActivitiesControllerRef = useRef(null);

  useEffect(() => {
    workspaceActivitiesCountRef.current = workspaceActivities.length;
  }, [workspaceActivities.length]);

  useEffect(() => {
    const controller = new AbortController();
    workspaceActivitiesControllerRef.current = controller;
    workspaceActivitiesGenerationRef.current += 1;
    workspaceActivitiesCountRef.current = 0;
    workspaceActivitiesLoadingMoreRef.current = false;
    workspaceActivitiesExhaustedRef.current = true;
    setWorkspaceActivities([]);
    setWorkspaceActivityTotals(null);
    setWorkspaceActivitiesHasMore(false);
    setWorkspaceActivitiesLoadingMore(false);
    setWorkspaceActivitiesRefreshing(false);
    setWorkspaceActivitiesError("");
    setWorkspaceActivitiesUpdatedAt(null);
    return () => controller.abort();
  }, [dateRange]);

  useEffect(() => {
    const syncCandidateImportWork = (event) => {
      setCandidateImportWork(event?.detail?.state || readCandidateImportWork());
    };
    window.addEventListener(CANDIDATE_IMPORT_WORK_EVENT, syncCandidateImportWork);
    window.addEventListener("storage", syncCandidateImportWork);
    return () => {
      window.removeEventListener(CANDIDATE_IMPORT_WORK_EVENT, syncCandidateImportWork);
      window.removeEventListener("storage", syncCandidateImportWork);
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetchRecruiterUsers({ apiBase: API_BASE })
      .then((users) => {
        if (active) setPlatformTeamUsers(Array.isArray(users) ? users : []);
      })
      .catch((error) => {
        // The current authenticated user remains available as a safe fallback.
        console.warn("Unable to load platform users for the Activities team filter:", error);
        if (active) setPlatformTeamUsers([]);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const companyProposalKinds = ["company_contact", "company_job", "company_lead"];
    const approvedIds = new Set(
      workspaceActivities
        .filter(
          (activity) =>
            activity?.entity_type === "company" &&
            companyProposalKinds.includes(activity?.metadata?.review_kind) &&
            activity?.metadata?.review_status === "approved"
        )
        .map((activity) => String(activity.id))
    );

    if (seenApprovedProposalIdsRef.current === null) {
      // First load: don't fire events for proposals that were already approved before this page opened.
      seenApprovedProposalIdsRef.current = approvedIds;
      return;
    }

    for (const activity of workspaceActivities) {
      const activityId = String(activity?.id);
      if (
        activity?.entity_type !== "company" ||
        !companyProposalKinds.includes(activity?.metadata?.review_kind) ||
        activity?.metadata?.review_status !== "approved" ||
        seenApprovedProposalIdsRef.current.has(activityId)
      ) {
        continue;
      }

      const refreshDetail = {
        companyId: Number(activity.entity_id),
        reviewKind: activity.metadata.review_kind,
        updatedAt: Date.now(),
      };
      try {
        sessionStorage.setItem("rpo:company-review-records-updated", JSON.stringify(refreshDetail));
      } catch {
        // The event below still refreshes an already-open company view.
      }
      window.dispatchEvent(
        new CustomEvent("rpo:company-review-records-updated", { detail: refreshDetail })
      );
    }
    seenApprovedProposalIdsRef.current = approvedIds;
  }, [workspaceActivities]);

  useEffect(() => {
    if (!pendingCompanyEnrichment?.name) return;
    const normalizedName = String(pendingCompanyEnrichment.name).trim().toLowerCase();
    const hasPersistedWorkflow = workspaceActivities.some((activity) => {
      const title = String(activity?.title || "").toLowerCase();
      return title.startsWith("enriching company:") && title.includes(normalizedName);
    });
    if (!hasPersistedWorkflow) return;
    try { sessionStorage.removeItem("rpo:pending-company-enrichment"); } catch {}
    setPendingCompanyEnrichment(null);
  }, [pendingCompanyEnrichment, workspaceActivities]);

  const refreshWorkspaceActivities = useCallback(async () => {
    const requestGeneration = workspaceActivitiesGenerationRef.current;
    setWorkspaceActivitiesRefreshing(true);
    try {
      const { data } = await fetchWorkspaceActivities({
        limit: Math.max(WORKSPACE_ACTIVITY_PAGE_SIZE, workspaceActivitiesCountRef.current),
        offset: 0,
        ...dateRange,
        signal: workspaceActivitiesControllerRef.current?.signal,
      });
      if (requestGeneration !== workspaceActivitiesGenerationRef.current) return;
      setWorkspaceActivities((current) => mergeActivitySnapshot(current, Array.isArray(data.activities) ? data.activities : []));
      setWorkspaceActivitiesHasMore(Boolean(data.hasMore));
      workspaceActivitiesExhaustedRef.current = !data.hasMore;
      setWorkspaceActivityTotals(data?.totals && typeof data.totals === "object" ? data.totals : null);
      setWorkspaceActivitiesError("");
      setWorkspaceActivitiesUpdatedAt(new Date());
    } catch (error) {
      if (requestGeneration !== workspaceActivitiesGenerationRef.current || error?.name === "AbortError") return;
      console.error("Failed to load Activities board:", error);
      setWorkspaceActivitiesError(error?.message || "Unable to reach the activities service.");
    } finally {
      if (requestGeneration === workspaceActivitiesGenerationRef.current) setWorkspaceActivitiesRefreshing(false);
    }
  }, [dateRange]);

  const loadMoreWorkspaceActivities = useCallback(async () => {
    if (
      workspaceActivitiesLoadingMoreRef.current ||
      workspaceActivitiesLoadingMore ||
      !workspaceActivitiesHasMore ||
      workspaceActivitiesExhaustedRef.current
    ) {
      return;
    }

    workspaceActivitiesLoadingMoreRef.current = true;
    setWorkspaceActivitiesLoadingMore(true);
    const requestGeneration = workspaceActivitiesGenerationRef.current;
    try {
      const { data } = await fetchWorkspaceActivities({
        limit: WORKSPACE_ACTIVITY_PAGE_SIZE,
        offset: workspaceActivities.length,
        includeTotals: false,
        ...dateRange,
        signal: workspaceActivitiesControllerRef.current?.signal,
      });
      if (requestGeneration !== workspaceActivitiesGenerationRef.current) return;
      const incoming = Array.isArray(data.activities) ? data.activities : [];
      const knownIds = new Set(workspaceActivities.map((activity) => String(activity?.id || "")));
      const uniqueIncoming = incoming.filter((activity) => !knownIds.has(String(activity?.id || "")));
      const addedCount = uniqueIncoming.length;
      setWorkspaceActivities((current) => {
        const seen = new Set(current.map((activity) => String(activity?.id || "")));
        return [...current, ...uniqueIncoming.filter((activity) => !seen.has(String(activity?.id || "")))];
      });
      // A repeated page must never leave the loading sentinel eligible to
      // request that same page again forever.
      const hasMore = Boolean(data.hasMore) && addedCount > 0;
      workspaceActivitiesExhaustedRef.current = !hasMore;
      setWorkspaceActivitiesHasMore(hasMore);
      if (data?.totals && typeof data.totals === "object") {
        setWorkspaceActivityTotals(data.totals);
      }
    } catch (error) {
      if (requestGeneration !== workspaceActivitiesGenerationRef.current || error?.name === "AbortError") return;
      setWorkspaceActivitiesError(error?.message || "Unable to load more activities.");
    } finally {
      if (requestGeneration === workspaceActivitiesGenerationRef.current) {
        workspaceActivitiesLoadingMoreRef.current = false;
        setWorkspaceActivitiesLoadingMore(false);
      }
    }
  }, [dateRange, workspaceActivities, workspaceActivitiesHasMore, workspaceActivitiesLoadingMore]);

  // The board loads incrementally, but View all must finish loading the
  // remaining pages before its table can report a complete result count.
  useEffect(() => {
    if (!reviewItemsDialogType || !workspaceActivitiesHasMore || workspaceActivitiesError) return;
    void loadMoreWorkspaceActivities();
  }, [reviewItemsDialogType, workspaceActivitiesHasMore, workspaceActivitiesError, loadMoreWorkspaceActivities]);

  useEffect(() => {
    const hasExperienceCompanyReview = workspaceActivities.some(
      (activity) => activity?.metadata?.review_kind === "candidate_experience_company"
    );
    if (!hasExperienceCompanyReview) return;

    let active = true;
    const loadCompanyDirectory = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/companies?page=1&limit=10&compact=1`, {
          credentials: "include",
          headers: getAuthHeaders(),
        });
        const data = await response.json().catch(() => ({}));
        if (!active || !response.ok) return;
        // GET /api/companies returns its paginated records under `rows`.
        // Keep the other shapes for compatibility with older deployments.
        const companies = Array.isArray(data)
          ? data
          : data?.rows || data?.companies || data?.data?.rows || data?.data || [];
        const next = new Map();
        companies.forEach((company) => {
          const name = String(company?.name || company?.company_name || "").trim();
          const id = Number(company?.id || company?.company_id || 0);
          if (!name || !id) return;
          next.set(name.toLowerCase(), {
            id,
            website: String(company?.website || company?.company_website || company?.career_site || "").trim(),
          });
        });
        setCompanyDirectoryByName(next);
      } catch {
        // The review cards still render without live directory enrichment.
      }
    };
    void loadCompanyDirectory();
    return () => { active = false; };
  }, [workspaceActivities]);

  useEffect(() => {
    const candidateIds = [...new Set(
      workspaceActivities
        .filter((activity) => activity?.metadata?.review_kind === "candidate_experience_company")
        .map((activity) => Number(activity?.entity_id || 0))
        .filter((id) => Number.isFinite(id) && id > 0)
    )];
    if (!candidateIds.length) return;

    let active = true;
    const loadExperienceCompanyWebsites = async () => {
      try {
        const responses = await Promise.all(candidateIds.map(async (candidateId) => {
          const response = await fetch(`${API_BASE}/api/candidates/${encodeURIComponent(candidateId)}`, {
            credentials: "include",
            headers: getAuthHeaders(),
          });
          const data = await response.json().catch(() => ({}));
          return response.ok ? (data?.candidate || data?.data || data) : null;
        }));
        if (!active) return;
        const next = new Map();
        responses.filter(Boolean).forEach((candidate) => {
          let experience = candidate?.experience_items || candidate?.experience || [];
          if (typeof experience === "string") {
            try { experience = JSON.parse(experience); } catch { experience = []; }
          }
          (Array.isArray(experience) ? experience : []).forEach((item) => {
            const name = String(item?.company || item?.company_name || item?.employer || "").trim();
            const website = String(item?.company_website || item?.companyWebsite || item?.website || item?.url || "").trim();
            if (name && /^https?:\/\//i.test(website)) next.set(name.toLowerCase(), website);
          });
        });
        setCandidateReviewCompanyWebsites(next);
      } catch {
        // Cards without a parsed company URL remain readable but unlinked.
      }
    };
    void loadExperienceCompanyWebsites();
    return () => { active = false; };
  }, [workspaceActivities]);

  const deleteAllActivities = useCallback(async () => {
    if (!window.confirm("Delete all activities? This cannot be undone.")) return;

    setDeletingAllActivities(true);
    try {
      // Invalidate any outstanding board refresh before issuing the delete.
      workspaceActivitiesGenerationRef.current += 1;
      const response = await fetch(`${API_BASE}/api/activities?delete_activities=true`, {
        method: "DELETE",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || `Unable to delete activities (${response.status}).`);
      }
      setWorkspaceActivities([]);
      setWorkspaceActivityTotals({ review: 0, progress: 0, complete: 0, failed: 0, rejected: 0 });
      setWorkspaceActivitiesError("");
      setWorkspaceActivitiesUpdatedAt(new Date());
    } catch (error) {
      console.error("Failed to delete all activities:", error);
      setWorkspaceActivitiesError(error?.message || "Unable to delete all activities.");
    } finally {
      setDeletingAllActivities(false);
    }
  }, []);

  const hasActiveWorkspaceWorkflow = useMemo(
    () =>
      workspaceActivities.some((activity) => activityBoardState(activity?.metadata) === "progress"),
    [workspaceActivities]
  );

  const entityActivityTotals = useMemo(() => selectActivityTotals(workspaceActivityTotals, filterValues.entity), [workspaceActivityTotals, filterValues.entity]);
  const liveMetricCards = useMemo(() => {
    const totals = { review: 0, progress: 0, complete: 0, failed: 0, rejected: 0, mine: 0 };
    const activeAgents = new Set();
    const currentUserKey = String(currentUserName || "").trim().toLowerCase();
    workspaceActivities.forEach((activity) => {
      const metadata = activity?.metadata || {};
      const state = activityBoardState(metadata);
      totals[state] += 1;
      if (state === "progress") activeAgents.add(String(activity?.actor_name || "AI / System"));
      if (currentUserKey && String(activity?.actor_name || "").trim().toLowerCase() === currentUserKey) totals.mine += 1;
    });
    // Activity cards are paged. Prefer the server aggregates so the summary
    // describes the whole board rather than changing as more cards are read.
    const reported = entityActivityTotals || {};
    const totalOrFallback = (key, fallback) => {
      const value = Number(reported[key]);
      return Number.isFinite(value) && value >= 0 ? value : fallback;
    };
    const values = {
      "Needs Review": totalOrFallback("review", totals.review),
      "In Progress": totalOrFallback("progress", totals.progress),
      Completed: totalOrFallback("complete", totals.complete),
      Failed: totalOrFallback("failed", totals.failed),
      "Active Agents": totalOrFallback("activeAgents", activeAgents.size),
      "Open Tasks": totalOrFallback("openTasks", totals.review + totals.progress),
      "My Activities": totalOrFallback("mine", totals.mine),
    };
    return metricCards.map((item) => ({ ...item, value: String(values[item.title] ?? 0), detail: "Live data" }));
  }, [currentUserName, workspaceActivities, entityActivityTotals]);

  const matchesActivityFilters = useCallback((card) => {
    const isAll = (key, value) => !value || value === filterConfigs.find((item) => item.key === key)?.value;
    if (!isAll("status", filterValues.status)) {
      const statusMap = { "Needs Review": "review", "In Progress": "progress", Completed: "complete", Rejected: "rejected", Failed: "failed" };
      if (card.state !== statusMap[filterValues.status]) return false;
    }
    if (!isAll("entity", filterValues.entity)) {
      if (!matchesActivityEntity(card, filterValues.entity)) return false;
    }
    if (!isAll("source", filterValues.source) && (card.sourceCategory || activitySource(card)) !== filterValues.source) return false;
    if (!isAll("priority", filterValues.priority) && (card.priorityCategory || activityPriorityCategory(card)) !== filterValues.priority) return false;
    if (!isAll("type", filterValues.type) && (card.typeCategory || activityTypeCategory(card)) !== filterValues.type) return false;
    if (!matchesActivityDate(card, filterValues.date)) return false;
    return true;
  }, [filterValues]);

  // Empty filtered columns have no scrolling sentinel. Read remaining pages
  // so matching records beyond the first page can still reach the board.
  useEffect(() => {
    const hasFilters = filterConfigs.some(config => config.key !== "date" && filterValues[config.key] !== config.value);
    if (hasFilters && workspaceActivitiesHasMore && !workspaceActivitiesError) {
      void loadMoreWorkspaceActivities();
    }
  }, [filterValues, workspaceActivitiesHasMore, workspaceActivitiesError, loadMoreWorkspaceActivities]);

  const createActivity = useCallback(async () => {
    const entityId = Number(newActivityForm.entityId);
    if (!Number.isFinite(entityId) || entityId <= 0 || !newActivityForm.title.trim()) {
      setWorkspaceActivitiesError("Enter an entity ID and activity title.");
      return;
    }
    setNewActivitySaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/activities`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ entityType: newActivityForm.entityType, entityId, title: newActivityForm.title, details: newActivityForm.details, activityType: "action" }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || "Unable to create activity.");
      setNewActivityOpen(false);
      setNewActivityForm({ entityType: "company", entityId: "", title: "", details: "" });
      await refreshWorkspaceActivities();
    } catch (error) {
      setWorkspaceActivitiesError(error?.message || "Unable to create activity.");
    } finally {
      setNewActivitySaving(false);
    }
  }, [newActivityForm, refreshWorkspaceActivities]);

  useEffect(() => {
    let active = true;
    let loading = false;
    const hasQueuedCandidateImport = Number(candidateImportWork?.activityId || 0) > 0;
    const load = async () => {
      if (loading) return;
      loading = true;
      const requestGeneration = workspaceActivitiesGenerationRef.current;
      try {
        const { data } = await fetchWorkspaceActivities({
          // Keep loaded cards available during polling instead of replacing
          // them with only page one of a long activity history.
          limit: Math.max(WORKSPACE_ACTIVITY_PAGE_SIZE, workspaceActivitiesCountRef.current),
          offset: 0,
          ...dateRange,
          signal: workspaceActivitiesControllerRef.current?.signal,
        });
        if (!active || requestGeneration !== workspaceActivitiesGenerationRef.current) return;
        setWorkspaceActivities((current) => mergeActivitySnapshot(current, Array.isArray(data.activities) ? data.activities : []));
        setWorkspaceActivitiesHasMore(Boolean(data.hasMore));
        workspaceActivitiesExhaustedRef.current = !data.hasMore;
        setWorkspaceActivityTotals(data?.totals && typeof data.totals === "object" ? data.totals : null);
        setWorkspaceActivitiesError("");
        setWorkspaceActivitiesUpdatedAt(new Date());
      } catch (error) {
        if (!active || requestGeneration !== workspaceActivitiesGenerationRef.current || error?.name === "AbortError") return;
        console.error("Failed to load Activities board:", error);
        if (active) setWorkspaceActivitiesError(error?.message || "Unable to reach the activities service.");
      } finally {
        loading = false;
      }
    };
    load();
    const timer = window.setInterval(
      load,
      // The optimistic CV card is a real queued activity, but it is not in
      // workspaceActivities until the first fetch completes. Poll it quickly
      // so its Completed transition is reflected without a 10-second gap.
      hasActiveWorkspaceWorkflow || pendingCompanyEnrichment || hasQueuedCandidateImport ? 750 : 10000
    );
    return () => { active = false; window.clearInterval(timer); };
  }, [dateRange, candidateImportWork?.activityId, hasActiveWorkspaceWorkflow, pendingCompanyEnrichment]);

  useEffect(() => {
    const activityId = Number(candidateImportWork?.activityId || 0);
    if (!Number.isFinite(activityId) || activityId <= 0) return;
    const matchingActivity = workspaceActivities.find((activity) => Number(activity?.id) === activityId);
    const status = String(
      matchingActivity?.metadata?.workflow_status || matchingActivity?.metadata?.status || ""
    ).toLowerCase();
    if (["completed", "failed", "stopped"].includes(status)) {
      clearCandidateImportWork();
    }
  }, [candidateImportWork?.activityId, workspaceActivities]);

  useEffect(() => {
    const refreshWhenReturningToActivities = () => {
      if (document.visibilityState !== "visible") return;
      void refreshWorkspaceActivities();
    };
    const refreshFromCompanyDiscovery = () => {
      try {
        const stored = JSON.parse(sessionStorage.getItem("rpo:pending-company-enrichment") || "null");
        setPendingCompanyEnrichment(stored?.name ? stored : null);
      } catch {}
      void refreshWorkspaceActivities();
    };
    const refreshOnVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshWhenReturningToActivities();
      }
    };

    window.addEventListener("pageshow", refreshWhenReturningToActivities);
    window.addEventListener("popstate", refreshWhenReturningToActivities);
    window.addEventListener("focus", refreshWhenReturningToActivities);
    window.addEventListener("rpo:activities-refresh", refreshFromCompanyDiscovery);
    const schedulingRefreshTimer = window.setInterval(refreshWhenReturningToActivities, 20000);
    document.addEventListener("visibilitychange", refreshOnVisibilityChange);
    return () => {
      window.removeEventListener("pageshow", refreshWhenReturningToActivities);
      window.removeEventListener("popstate", refreshWhenReturningToActivities);
      window.removeEventListener("focus", refreshWhenReturningToActivities);
      window.removeEventListener("rpo:activities-refresh", refreshFromCompanyDiscovery);
      window.clearInterval(schedulingRefreshTimer);
      document.removeEventListener("visibilitychange", refreshOnVisibilityChange);
    };
  }, [refreshWorkspaceActivities]);

  const teamMembers = useMemo(
    () => normalizePlatformTeamMembers(platformTeamUsers, storedUser),
    [platformTeamUsers, storedUser]
  );
  const toggleTeamMember = useCallback((id) => {
    setSelectedTeamMemberIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id]
    );
  }, []);

  const filteredThreads = useMemo(() => {
    const query = chatSearch.trim().toLowerCase();
    if (!query) return chatThreads;
    return chatThreads.filter((thread) =>
      [thread.title, thread.subtitle, thread.detail].some((value) =>
        String(value || "").toLowerCase().includes(query)
      )
    );
  }, [chatSearch]);

  const groupedThreads = useMemo(() => {
    return filteredThreads.reduce((groups, item) => {
      if (!groups[item.section]) groups[item.section] = [];
      groups[item.section].push(item);
      return groups;
    }, {});
  }, [filteredThreads]);

  const visibleColumnGroups = useMemo(() => {
      let dynamicCards = workspaceActivities.map((activity) => {
        const metadata = activity?.metadata && typeof activity.metadata === "object" ? activity.metadata : {};
        const proposedRecord =
          metadata?.proposed_job && typeof metadata.proposed_job === "object"
            ? metadata.proposed_job
            : metadata?.proposed_contact && typeof metadata.proposed_contact === "object"
              ? metadata.proposed_contact
              : {};
        const reviewKind = String(metadata.review_kind || "");
        const isContactApproval = reviewKind === "company_contact";
        const isCandidateCompanyProposal = reviewKind === "candidate_experience_company";
        const reviewSourceLabel = getReviewSourceLabel(metadata, proposedRecord);
        const isCareerSiteLead =
          reviewKind === "company_lead" ||
          (reviewKind === "company_job" && /career(?:s)?\s+site/i.test(reviewSourceLabel));
        const approvedRecordType = isCandidateCompanyProposal
          ? "company"
          : isContactApproval
            ? "contact"
            : isCareerSiteLead
              ? "lead"
              : "job";
        const companyTab = isContactApproval ? "contacts" : isCareerSiteLead ? "leads" : reviewKind === "company_job" ? "jobs" : "details";
        const approvedRecordName = String(
          isCandidateCompanyProposal
            ? metadata.proposed_company_name || "selected company"
            : isContactApproval
            ? proposedRecord?.name || proposedRecord?.full_name || proposedRecord?.contact_name || "selected contact"
            : proposedRecord?.job_title || proposedRecord?.title || "selected role"
        ).trim();
        const companyName = String(
          (isCandidateCompanyProposal ? metadata.proposed_company_name : "") ||
            activity?.entity_name ||
            activity?.company_name ||
            activity?.company ||
            proposedRecord?.company_name ||
            proposedRecord?.company ||
            ""
        ).trim();
        const directoryCompany = companyDirectoryByName.get(companyName.toLowerCase());
        const existingCompanyId = Number(metadata.existing_company_id || directoryCompany?.id || 0);
        const candidateCompanyWebsite = String(
          metadata.company_website ||
            directoryCompany?.website ||
            proposedRecord?.company_website ||
            proposedRecord?.website ||
            candidateReviewCompanyWebsites.get(companyName.toLowerCase()) ||
            ""
        ).trim();
        const companyReference = [
          companyName ? `Company: ${companyName}` : "",
          activity?.entity_type === "company" && activity?.entity_id ? `#${activity.entity_id}` : "",
        ].filter(Boolean).join(" · ");
        const ownerName = String(
          metadata.owner_name ||
          metadata.owner ||
          metadata.assigned_team_member ||
          activity.resolved_actor_name ||
          activity.actor_name ||
          "Unassigned"
        ).trim();
        const createdDateTime = formatActivityDateTime(activity.created_at);
        const reviewSource = reviewSourceLabel;
        const workflowStatus = String(metadata.workflow_status || metadata.status || "").toLowerCase();
        const progress = Number(metadata.progress ?? metadata.percent_complete ?? 0);
        const reviewStatus = String(metadata.review_status || "").trim().toLowerCase();
        const isRejectedReview = reviewStatus === "rejected";
        const isBeingApproved = approvingActivityIds.has(String(activity?.id));
        const isBeingStopped = stoppingActivityIds.has(String(activity?.id));
        const isApprovingOnServer = workflowStatus === "approving";
        const isCompanyProposal = ["company_contact", "company_job", "company_lead"].includes(reviewKind);
        // Job and contact proposals are actionable as soon as discovery finds
        // them. Keep the parent company scan in progress, but place each
        // individual proposal in Needs Review instead of withholding it until
        // the entire scan has completed.
        // Approval is terminal for an individual review proposal. Prefer that
        // persisted decision over a stale workflow status returned while the
        // board is refreshing, so approved contacts, jobs, and leads always
        // move to Completed.
        const isFailedWorkflow = workflowStatus === "failed";
        const rawEntityStatus = String(activity.current_recruiting_stage || activity.entity_status || "").trim();
        const entityStatus = activity.entity_type === "lead"
          ? LEAD_STATUS_VALUES.find((value) => value.toLowerCase() === rawEntityStatus.toLowerCase()) ||
            ({ active: "Client", "no stage": "New" }[rawEntityStatus.toLowerCase()]) || rawEntityStatus
          : rawEntityStatus;
        const state = activityBoardState(metadata, { approving: isBeingApproved, stopping: isBeingStopped });
        return {
        id: activity.id,
        entityType: activity.entity_type,
        entityId: activity.entity_id,
        currentRecruitingStage: activity.current_recruiting_stage,
        contactName: String(activity.contact_name || metadata.contact_name || "").trim(),
        leadTitle: [activity.lead_title, metadata.proposed_job?.job_title,
          metadata.proposed_job?.title, metadata.lead_title, metadata.job_title,
          /^Review lead:\s*/i.test(activity.title || "") ? activity.title.replace(/^Review lead:\s*/i, "") : ""]
          .map((value) => String(value || "").trim()).find(Boolean) || "",
        leadActivityDescription: String(activity.details || activity.summary ||
          (!/^Review lead:\s*/i.test(activity.title || "") ? activity.title : "") || "").trim(),
        jobTitle: [activity.job_title, metadata.proposed_job?.job_title,
          metadata.proposed_job?.title, metadata.job_title]
          .map((value) => String(value || "").trim()).find(Boolean) || "",
        contactTitle: isContactApproval || activity.entity_type === "contact"
          ? [metadata.proposed_contact?.job_title, metadata.proposed_contact?.title,
              metadata.proposed_contact?.role, activity.contact_title, metadata.contact_title]
              .map((value) => String(value || "").trim()).find(Boolean) || ""
          : undefined,
        actorUserId: String(
          activity.actor_user_id ??
          metadata.owner_user_id ??
          metadata.ownerUserId ??
          metadata.assigned_team_member_id ??
          metadata.user_id ??
          ""
        ).trim(),
        canManageMeeting: String(activity.actor_user_id) === String(storedUser?.id ?? storedUser?.user_id),
        actorName: ownerName,
        sourceCategory: activitySource(activity),
        typeCategory: activityTypeCategory(activity),
        priorityCategory: activityPriorityCategory(activity),
        activityType: activity.activity_type || "",
        createdAt: activity.created_at || "",
        activityTitle: String(activity.title || "").trim(),
        workflowStatus,
        company: {
          id: activity.entity_type === "company" ? activity.entity_id : existingCompanyId || null,
          name: companyName,
          tab: companyTab,
        },
        companyLink: isCandidateCompanyProposal
          ? {
              name: companyName,
              existingId: existingCompanyId || null,
              website: candidateCompanyWebsite,
            }
          : null,
        candidate: {
          id: activity.entity_type === "candidate" ? activity.entity_id : null,
          name: activity.candidate_name || metadata.candidate_name || "",
        },
        candidates: getActivityCandidates(activity, metadata),
        metadata,
        reviewEntityType:
          reviewKind === "company_contact"
            ? "contact"
            : isCareerSiteLead
              ? "lead"
              : reviewKind === "company_job"
                ? "job"
              : reviewKind === "candidate_experience_company"
                ? "company"
                : activity.entity_type || "job",
        state,
        completedSortTime: activityCompletionTime(activity),
        title: isBeingStopped
          ? `Stopping: ${activity.title || "Activity"}`
          : state === "progress" && (isBeingApproved || isApprovingOnServer)
          ? `Approving ${approvedRecordType}: ${approvedRecordName || activity.title || approvedRecordType}`
          : isCompanyProposal && state === "progress"
            ? `Discovering ${approvedRecordType}: ${approvedRecordName || activity.title || approvedRecordType}`
          : isCareerSiteLead && state === "review"
            ? `Review lead: ${approvedRecordName || "selected role"}`
          : activity.title || "Activity",
        subtitle: state === "progress" && (isBeingApproved || isApprovingOnServer)
          ? activity.details || "Saving the approved record to the database."
          : isCompanyProposal && state === "progress"
            ? `AI is validating this ${approvedRecordType} before it is ready for review.`
          : isCareerSiteLead && state === "review"
            ? "AI discovered this lead on the existing client’s careers site."
          : activity.summary || activity.details || "",
        completionDetails: state === "complete" && activity.summary &&
          String(activity.details || "").trim() !== String(activity.summary).trim()
          ? String(activity.details || "").trim()
          : "",
        detail: companyReference || (activity.entity_type ? `${activity.entity_type} #${activity.entity_id}` : ""),
          meta: [
            `Owner: ${ownerName}`,
            ...(activity.current_sequence_status ? [`Sequence: ${activity.current_sequence_name || metadata.sequence_name} (${activity.current_sequence_status})`] : []),
            createdDateTime,
          ],
        ...(state === "progress" ? {
          progress: isBeingApproved
            ? Math.max(15, Number.isFinite(progress) ? progress : 15)
            : Number.isFinite(progress) ? Math.max(0, Math.min(100, progress)) : isCompanyProposal ? 60 : 0,
          status: { label: isBeingStopped ? "Stopping" : isBeingApproved || isApprovingOnServer ? "Approving" : "Running", tone: "success" },
          actions: isBeingStopped ? [] : ["View Progress", "Stop"],
        } : state === "review" ? {
          actions: reviewKind === "interview_schedule" ? ["Schedule", "Dismiss"] : ["View", "Approve", "Reject"],
          priority: { label: "Review", tone: "warning" },
          reviewContext: [
            `Source: ${reviewSource}`,
            `Owner: ${ownerName}`,
            `Created: ${createdDateTime}`,
          ],
        } : {
          completedAt: activity.updated_at || activity.created_at
            ? isRejectedReview
              ? "Rejected from Needs Review"
              : isFailedWorkflow
                ? "Failed"
                : entityStatus ? `Status: ${entityStatus}` : "Status unavailable"
            : "",
          actions: metadata.calendar_event_id ? ["View"] : ["View", "Reopen"],
          priority: isRejectedReview
            ? { label: "Rejected", tone: "danger" }
            : isFailedWorkflow
              ? { label: "Failed", tone: "danger" }
              : { label: entityStatus || "Status unavailable", tone: "neutral" },
        }),
      };
      });
      // Immediately bridge the interval before the first workspace refresh.
      // Unlike the old temporary card, this is rendered only when the backend
      // has returned a real activity ID, so its Stop action is always valid.
      const candidateImportActivityId = Number(candidateImportWork?.activityId || 0);
      const candidateImportCandidateId = String(candidateImportWork?.candidateId || "").trim();
      const hasPersistedCandidateParse = dynamicCards.some(
        (card) =>
          card?.entityType === "candidate" &&
          card?.activityTitle === "Parsing candidate CV" &&
          String(card?.id || "") === String(candidateImportActivityId)
      );
      if (candidateImportActivityId > 0 && !hasPersistedCandidateParse) {
        dynamicCards = [{
          id: candidateImportActivityId,
          entityType: "candidate",
          entityId: candidateImportCandidateId || null,
          actorUserId: String(storedUser?.id ?? storedUser?.user_id ?? "").trim(),
          actorName: currentUserName,
          activityType: "action",
          activityTitle: "Parsing candidate CV",
          createdAt: candidateImportWork?.startedAt || candidateImportWork?.updatedAt || new Date().toISOString(),
          workflowStatus: "queued",
          metadata: { workflow_status: "queued", progress: 0 },
          reviewEntityType: "candidate",
          state: "progress",
          completedSortTime: 0,
          title: "Parsing candidate CV",
          subtitle: "Candidate resume import",
          detail: candidateImportWork?.message || "Reading and parsing the uploaded resume.",
          meta: [`Owner: ${currentUserName}`, "Starting now"],
          progress: 0,
          status: { label: "Running", tone: "success" },
          actions: ["View Progress", "Stop"],
        }, ...dynamicCards];
      }
      if (pendingCompanyEnrichment?.name) {
        dynamicCards = [
          {
            id: "pending-company-enrichment",
            entityType: "company",
            entityId: null,
            actorUserId: String(storedUser?.id ?? storedUser?.user_id ?? "").trim(),
            actorName: "Current user",
            activityType: "action",
            createdAt: new Date(Number(pendingCompanyEnrichment.startedAt || Date.now())).toISOString(),
            workflowStatus: "running",
            metadata: { workflow_kind: "company_enrichment", progress: 10 },
            reviewEntityType: "company",
            state: "progress",
            completedSortTime: 0,
            title: `Enriching company: ${pendingCompanyEnrichment.name}`,
            subtitle: "AI is enriching the company profile and finding contacts and career opportunities for review.",
            detail: "Company import in progress",
            meta: ["Owner: Current user", "Starting now"],
            progress: 10,
            status: { label: "Running", tone: "success" },
            actions: ["View Progress", "Stop"],
          },
          ...dynamicCards,
        ];
      }
      const boardColumnOrder = filterValues.status === "Rejected"
        ? ["rejected"]
        : filterValues.status === "Failed"
          ? ["failed"]
          : ["review", "progress", "complete"];
      return boardColumnOrder.map((columnKey) => {
        const group = columnGroups.find((candidate) => candidate.key === columnKey) || (
          columnKey === "rejected"
            ? { key: "rejected", label: "Rejected", count: "0", accent: "#EF4444", cards: [] }
            : columnKey === "failed"
              ? { key: "failed", label: "Failed", count: "0", accent: "#EF4444", cards: [] }
              : null
        );
        if (!group) return null;
        const stateByGroup = { review: "review", progress: "progress", complete: "complete", rejected: "rejected", failed: "failed" };
        const candidateIdsWithResumeParse = new Set(
          dynamicCards
            .filter(
              (card) =>
                card?.entityType === "candidate" &&
                card?.activityTitle === "Parsing candidate CV" &&
                card?.workflowStatus !== "failed"
            )
            .map((card) => String(card.entityId))
        );
        const cards = dynamicCards
          .filter(Boolean)
          .filter((card) => card.state === stateByGroup[group.key])
          .filter(matchesActivityFilters)
          .filter((card) => {
            if (!selectedTeamMemberIds.length) return true;
            if (card.actorUserId) return selectedTeamMemberIds.includes(card.actorUserId);

            // Older activity records may predate actor_user_id. Keep those
            // records filterable by the matching platform user's name, without
            // ever adding names from candidate/contact data to the Team strip.
            const ownerName = String(card.actorName || "").trim().toLowerCase();
            if (!ownerName) return false;
            return teamMembers
              .filter((member) => selectedTeamMemberIds.includes(member.id))
              .some((member) => {
              const selectedName = String(member.name || "").trim().toLowerCase();
              return ownerName.includes(selectedName) || selectedName.includes(ownerName);
            });
          })
          .filter(
            (card) =>
              !(
                card?.entityType === "candidate" &&
                card?.activityTitle === "Candidate created" &&
                candidateIdsWithResumeParse.has(String(card.entityId))
              )
          )
          .sort((left, right) =>
            group.key === "complete" ? right.completedSortTime - left.completedSortTime || Number(right.id) - Number(left.id) : 0
          );
        const reportedTotal = Number(entityActivityTotals?.[group.key]);
        return {
          ...group,
          cards,
          allCards: dynamicCards.filter((card) => card?.state === stateByGroup[group.key]),
          // The board is paged, so card length only reflects rows fetched so
          // far. Use the server total in each status header while the column
          // continues revealing and fetching cards as it is scrolled.
          count: !workspaceActivitiesHasMore ? String(cards.length) : Number.isFinite(reportedTotal) && reportedTotal >= 0
            ? String(reportedTotal)
            : String(cards.length),
        };
      }).filter(Boolean);
    }, [approvingActivityIds, candidateImportWork, candidateReviewCompanyWebsites, companyDirectoryByName, currentUserName, filterValues.status, matchesActivityFilters, pendingCompanyEnrichment, selectedTeamMemberIds, stoppingActivityIds, storedUser, teamMembers, workspaceActivities, workspaceActivitiesHasMore, entityActivityTotals]);

  const openCompletedCompany = useCallback(
    async (company) => {
      const companyId = Number(company?.id);
      if (!Number.isFinite(companyId) || companyId <= 0) return;
      const tab = company?.tab || "details";

      // A stub {id, name} payload lacks the fields LeadTabsLayout uses to
      // recognize a company, and leaves the Details tab blank until its own
      // fetch resolves. Load the real record first so navigation opens with
      // the company's actual saved data already in place.
      let fullCompany = null;
      try {
        const response = await fetch(`${API_BASE}/api/companies/${companyId}/info`, {
          credentials: "include",
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          fullCompany = data?.company && typeof data.company === "object" ? data.company : null;
        }
      } catch (error) {
        console.error("Failed to load company before navigating:", error);
      }

      const payload = buildOpenCompanyNavPayload({
        company: {
          ...(fullCompany || {}),
          id: companyId,
          name: fullCompany?.name || company?.name || "Company",
          entityType: "company",
        },
        defaultTab: tab,
        returnListTab: "companies",
        entityType: "company",
      });
      navigate(
        `/companies?companyId=${encodeURIComponent(companyId)}&tab=${encodeURIComponent(tab)}&entityType=company&returnTab=companies`,
        { state: payload || undefined }
      );
    },
    [navigate]
  );

  const openCompletedCandidate = useCallback(
    (candidate) => {
      const candidateId = Number(candidate?.id);
      if (!Number.isFinite(candidateId) || candidateId <= 0) return;
      navigate(`/candidates?candidateId=${encodeURIComponent(candidateId)}&tab=details`);
    },
    [navigate]
  );

  const updateReviewActivity = async (item, decision, { refresh = true, primaryCompanyName = "" } = {}) => {
    const isCandidateCompanyProposal =
      item?.entityType === "candidate" &&
      item?.metadata?.review_kind === "candidate_experience_company";
    const isCompanyGeneratedProposal =
      item?.entityType === "company" &&
      ["company_contact", "company_job", "company_lead"].includes(item?.metadata?.review_kind);
    const isCompanyProposalApproval =
      decision === "approve" &&
      item?.entityType === "company" &&
      ["company_contact", "company_job", "company_lead"].includes(item?.metadata?.review_kind);
    const url =
      decision === "approve" && isCandidateCompanyProposal
        ? `${API_BASE}/api/candidates/${item.entityId}/company-proposals/${item.id}/approve`
        : decision === "approve" && isCompanyGeneratedProposal
          ? `${API_BASE}/api/companies/${item.entityId}/review-proposals/${item.id}/approve`
        : `${API_BASE}/api/activities/${item.id}`;
    const body =
      decision === "approve" && isCandidateCompanyProposal
        ? { primary_company_name: primaryCompanyName }
        : decision === "approve" && isCompanyGeneratedProposal
        ? undefined
        : {
            metadata: {
              ...item.metadata,
              review_status: decision === "approve" ? "approved" : "rejected",
              workflow_status: "completed",
              progress: 100,
            },
          };
    if (isCompanyProposalApproval) {
      setApprovingActivityIds((current) => new Set(current).add(String(item.id)));
    }

    try {
    const response = await fetch(url, {
      method: decision === "approve" && (isCandidateCompanyProposal || isCompanyGeneratedProposal)
        ? "POST"
        : "PUT",
      credentials: "include",
      headers: body
        ? { "Content-Type": "application/json", ...getAuthHeaders() }
        : getAuthHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.error || "Failed to update review activity");
    }
    const result = await response.json().catch(() => ({}));
    if (decision === "approve") {
      setWorkspaceActivities((current) => current.map((activity) => {
        if (String(activity?.id) !== String(item.id)) return activity;
        if (["approved", "rejected"].includes(activity.metadata?.review_status)) return activity;
        return {
          ...activity,
          metadata: {
            ...activity.metadata,
            ...(!result.queued ? { approved_at: result.activity?.metadata?.approved_at || new Date().toISOString() } : {}),
            review_status: result.queued ? "needs_review" : "approved",
            workflow_status: result.queued ? "approving" : "completed",
            progress: result.queued ? Math.max(15, Number(activity.metadata?.progress) || 0) : 100,
          },
        };
      }));
    }
    if (decision === "reject") {
      setWorkspaceActivities((current) =>
        current.map((activity) =>
          String(activity?.id) === String(item.id)
            ? {
                ...activity,
                updated_at: new Date().toISOString(),
                metadata: {
                  ...(activity?.metadata || {}),
                  ...item.metadata,
                  review_status: "rejected",
                  workflow_status: "completed",
                  progress: 100,
                },
              }
            : activity
        )
      );
    }
    if (refresh) await refreshWorkspaceActivities();
    } finally {
      if (isCompanyProposalApproval) {
        setApprovingActivityIds((current) => {
          const next = new Set(current);
          next.delete(String(item.id));
          return next;
        });
      }
    }
  };

  const handleReviewApproval = async (item, decision, options = {}) => {
    if (decision === "approve" && item.metadata?.review_kind === "interview_schedule") {
      openInterviewScheduler({ candidateId: item.metadata.candidate_id || item.entityId, jobId: item.metadata.job_id,
        stage: item.metadata.next_stage, scheduleTaskId: item.id });
      return;
    }
    if (decision === "dismiss" && item.metadata?.review_kind === "interview_schedule") {
      try {
        await schedulingRequest(`/activities/${item.id}`, { method: "PUT", body: JSON.stringify({
          metadata: { ...item.metadata, review_status: "approved", workflow_status: "completed", task_status: "dismissed" },
          details: "Scheduling task dismissed; handled manually.",
        }) });
        interviewsChanged();
        await refreshWorkspaceActivities();
      } catch (error) { setWorkspaceActivitiesError(error.message); }
      return;
    }
    const primaryCompanyOptions =
      decision === "approve" && item?.metadata?.review_kind === "candidate_experience_company"
        ? getPrimaryCompanyOptions(item)
        : [];
    if (primaryCompanyOptions.length) {
      setPrimaryCompanySelection(item);
      setSelectedPrimaryCompanyName(primaryCompanyOptions[0]);
      return;
    }
    return updateReviewActivity(item, decision, options);
  };

  const confirmReviewItemRejection = async () => {
    if (!reviewItemPendingRejection) return;
    try {
      await updateReviewActivity(reviewItemPendingRejection, "reject");
      setReviewItemPendingRejection(null);
    } catch (error) {
      console.error("Failed to reject review activity:", error);
    }
  };

  const stopInProgressActivity = useCallback(async (item) => {
    if (!item?.id) return;
    const activityId = String(item.id);
    if (activityId === "pending-company-enrichment") {
      try { sessionStorage.removeItem("rpo:pending-company-enrichment"); } catch {}
      setPendingCompanyEnrichment(null);
      return;
    }
    setStoppingActivityIds((current) => new Set(current).add(activityId));
    try {
      const response = await fetch(`${API_BASE}/api/activities/${encodeURIComponent(activityId)}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          metadata: {
            ...(item.metadata || {}),
            review_status: "needs_review",
            workflow_status: "stopped",
            progress: 0,
            assigned_agent: null,
          },
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || "Unable to stop this activity.");
      }
      await refreshWorkspaceActivities();
    } catch (error) {
      setWorkspaceActivitiesError(error?.message || "Unable to stop this activity.");
    } finally {
      setStoppingActivityIds((current) => {
        const next = new Set(current);
        next.delete(activityId);
        return next;
      });
    }
  }, [refreshWorkspaceActivities]);

  const reviewItemsForDialog = useMemo(() => {
    return selectActivityDialogItems(visibleColumnGroups, itemsDialogGroup, reviewItemsDialogType);
  }, [reviewItemsDialogType, itemsDialogGroup, visibleColumnGroups]);

  const openReviewItemsDialog = useCallback((type, group) => {
    if (!type || !group) return;
    setWorkspaceActivitiesError("");
    setItemsDialogGroup(group);
    setReviewItemsDialogType(type);
    setSelectedReviewItemIds([]);
  }, [setReviewItemsDialogType]);

  const addSelectedReviewItemsToAgent = useCallback(async () => {
    const selectedItems = reviewItemsForDialog.filter((item) =>
      selectedReviewItemIds.includes(String(item.id))
    );
    if (!selectedItems.length) return;

    setAddingReviewItemsToAgent(true);
    try {
      await Promise.all(
        selectedItems.map(async (item) => {
          const response = await fetch(`${API_BASE}/api/activities/${encodeURIComponent(item.id)}`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json", ...getAuthHeaders() },
            body: JSON.stringify({
              metadata: {
                ...(item.metadata || {}),
                review_status: "approved",
                workflow_status: "in_progress",
                progress: 10,
                assigned_agent: "AI Agent",
              },
            }),
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload?.error || "Unable to add the selected item to the AI Agent.");
          }
        })
      );
      setReviewItemsDialogType("");
      setSelectedReviewItemIds([]);
      await refreshWorkspaceActivities();
    } catch (error) {
      setWorkspaceActivitiesError(error?.message || "Unable to add the selected items to the AI Agent.");
    } finally {
      setAddingReviewItemsToAgent(false);
    }
  }, [refreshWorkspaceActivities, reviewItemsForDialog, selectedReviewItemIds, setReviewItemsDialogType]);

  const bulkRejectSelectedReviewItems = async () => {
    const selectedItems = reviewItemsForDialog.filter((item) =>
      selectedReviewItemIds.includes(String(item.id))
    );
    if (!selectedItems.length) return;

    setBulkRejectingReviewItems(true);
    try {
      await Promise.all(selectedItems.map((item) => updateReviewActivity(item, "reject", { refresh: false })));
      setSelectedReviewItemIds([]);
      await refreshWorkspaceActivities();
    } catch (error) {
      console.error("Failed to reject selected review activities:", error);
      setWorkspaceActivitiesError(error?.message || "Unable to reject the selected review items.");
    } finally {
      setBulkRejectingReviewItems(false);
    }
  };

  return (
    <ListTableTabsTemplate
      breadcrumb={
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ px: 0.4, pt: 0.4 }}
        >
          <Box sx={{ minWidth: 0, "& .breadcrumb": { mb: 0 } }}>
            <Breadcrumb
              items={[
                { label: "Overview", onClick: () => navigate("/overview") },
                { label: "Activities" },
              ]}
            />
            {workspaceActivitiesError ? (
              <Typography sx={{ fontSize: 12.5, color: RED, fontWeight: 600, mt: 0.5 }}>
                {workspaceActivitiesError}
              </Typography>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap justifyContent={{ xs: "flex-start", md: "flex-end" }} alignItems="center" sx={{ maxWidth: { md: "72%" } }}>
          <ActionButton label="New Activity" icon={<AddRoundedIcon />} onClick={() => setNewActivityOpen(true)} />
          <InterviewCalendar />
          <ActionButton label="Launch Agent" icon={<SmartToyOutlinedIcon />} onClick={() => { setAgentPrompt(""); setAgentDialogOpen(true); }} />
          <ActionButton label="Agent History" icon={<HistoryOutlinedIcon />} onClick={() => { setAgentPrompt("Show my agent history and recent workflow results."); setAgentDialogOpen(true); }} />
          <ActionButton label="Continue Agent" icon={<PlayCircleOutlineRoundedIcon />} onClick={() => { setAgentPrompt("Continue the pending agent workflow and report its current status."); setAgentDialogOpen(true); }} />
          <ActionButton
            label={deletingAllActivities ? "Deleting…" : "Delete all activities"}
            icon={<HighlightOffRoundedIcon />}
            onClick={deleteAllActivities}
            disabled={deletingAllActivities}
            color={RED}
          />
          <IconButton
            aria-label="Refresh activities"
            title="Refresh activities"
            onClick={refreshWorkspaceActivities}
            disabled={workspaceActivitiesRefreshing}
            sx={{
              width: 40,
              height: 40,
              border: `1px solid ${BORDER}`,
              borderRadius: "10px",
              color: BLUE,
              "&:hover": { backgroundColor: alpha(BLUE, 0.08) },
            }}
          >
            <AutorenewRoundedIcon
              sx={{
                fontSize: 19,
                animation: workspaceActivitiesRefreshing ? "spin 0.85s linear infinite" : "none",
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>
          {hasActiveWorkspaceWorkflow ? (
            <Typography sx={{ fontSize: 12, color: BLUE, fontWeight: 600 }}>
              Updating every 2.5 seconds
              {workspaceActivitiesUpdatedAt
                ? ` · Checked ${workspaceActivitiesUpdatedAt.toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                    second: "2-digit",
                  })}`
                : ""}
            </Typography>
          ) : null}
        </Stack>
        </Stack>
      }
      rootSx={{ width: "100%", maxWidth: "100%" }}
      shellSx={{ pb: 0, border: "0 !important", borderRadius: "0 !important", backgroundColor: "transparent !important", overflow: "visible" }}
      leftColumnSx={{ backgroundColor: "transparent" }}
      contentSection={<Box
        sx={{
          px: 0.4,
          pt: 1.25,
          pb: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
        }}
      >

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
            xl: "repeat(8, minmax(0, 1fr))",
          },
          gap: 1.2,
        }}
      >
        {liveMetricCards.map((item) => (
          <StatCard key={item.title} item={item} />
        ))}
        <InsightCard />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 1fr)",
            xl: "minmax(0, 1fr)",
          },
          gap: 1.25,
          alignItems: "start",
        }}
      >
        {false ? <Box sx={{ ...panelSx, p: 1.45 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: TEXT }}>Chats</Typography>
            <IconButton sx={{ width: 30, height: 30, color: PURPLE }}>
              <AddRoundedIcon />
            </IconButton>
          </Stack>

          <TextField
            fullWidth
            size="small"
            value={chatSearch}
            onChange={(event) => setChatSearch(event.target.value)}
            placeholder="Search chats..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: SUBTLE }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.35,
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: "#FAFBFF",
              },
            }}
          />

          <Stack divider={<Divider flexItem sx={{ borderColor: "#EEF2FF" }} />}>
            {Object.entries(groupedThreads).map(([section, items]) => (
              <Box key={section} sx={{ py: 0.4 }}>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    color: MUTED,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    mb: 0.35,
                  }}
                >
                  {section}
                </Typography>
                {items.map((item) => (
                  <ChatThreadRow key={`${section}-${item.title}-${item.time}`} item={item} />
                ))}
              </Box>
            ))}
          </Stack>

          <Button
            endIcon={<KeyboardArrowDownRoundedIcon />}
            sx={{
              mt: 1.3,
              width: "100%",
              borderRadius: "14px",
              border: `1px solid ${BORDER}`,
              color: TEXT,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Show more
          </Button>
        </Box> : null}

        <Stack spacing={1.25}>
          <Box sx={{ ...panelSx, p: 1.35 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  xl: "repeat(7, minmax(0, 1fr)) auto",
                },
                gap: 1.05,
                alignItems: "end",
              }}
            >
              {filterConfigs.map((item) => (
                <FilterSelect
                  key={item.key}
                  label={item.label}
                  value={filterValues[item.key]}
                  options={item.options}
                  onChange={(value) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      [item.key]: value,
                    }))
                  }
                />
              ))}
              <IconButton
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "14px",
                  border: `1px solid ${BORDER}`,
                  color: TEXT,
                  alignSelf: "end",
                }}
              >
                <TuneRoundedIcon />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr)",
              gap: 1.2,
            }}
          >
            <Box sx={{ ...panelSx, p: 1.5 }}>
              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 1.3 }}>
                <Typography sx={{ fontSize: 21, fontWeight: 800, color: TEXT }}>
                  Team
                </Typography>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  (Filter by user)
                </Typography>
                <InfoOutlinedIcon sx={{ fontSize: 15, color: MUTED }} />
              </Stack>

              <Stack direction="row" spacing={1.15} alignItems="center" flexWrap="wrap">
                <Chip
                  label="All"
                  onClick={() => setSelectedTeamMemberIds([])}
                  sx={{
                    borderRadius: "999px",
                    cursor: "pointer",
                    bgcolor: selectedTeamMemberIds.length === 0 ? alpha(PURPLE, 0.12) : "#FFFFFF",
                    color: selectedTeamMemberIds.length === 0 ? PURPLE : MUTED,
                    border: selectedTeamMemberIds.length === 0 ? `1px solid ${alpha(PURPLE, 0.32)}` : `1px solid ${BORDER}`,
                    fontWeight: 700,
                  }}
                />
                {teamMembers.map((item) => (
                  <TeamAvatar
                    key={item.id}
                    item={item}
                    selected={selectedTeamMemberIds.includes(item.id)}
                    onClick={() => toggleTeamMember(item.id)}
                  />
                ))}
                <Stack spacing={0.75} alignItems="center">
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "14px",
                      border: `1px dashed ${alpha(PURPLE, 0.4)}`,
                      color: PURPLE,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AddRoundedIcon />
                  </Box>
                  <Typography sx={{ fontSize: 12, color: MUTED }}>Add User</Typography>
                </Stack>
              </Stack>
            </Box>

            {false ? <Box sx={{ ...panelSx, p: 1.5 }}>
              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 1.3 }}>
                <Typography sx={{ fontSize: 21, fontWeight: 800, color: TEXT }}>
                  Agents
                </Typography>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  (Filter by agent)
                </Typography>
                <InfoOutlinedIcon sx={{ fontSize: 15, color: MUTED }} />
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label="All"
                  sx={{
                    borderRadius: "999px",
                    bgcolor: alpha(BLUE, 0.1),
                    color: BLUE,
                    fontWeight: 700,
                  }}
                />
                {agentItems.map((item) => (
                  <AgentPill key={item.name} item={item} />
                ))}
                <Button
                  startIcon={<AddRoundedIcon />}
                  sx={{
                    textTransform: "none",
                    color: MUTED,
                    fontWeight: 600,
                    minHeight: 40,
                  }}
                >
                  Add Agent
                </Button>
              </Stack>
            </Box> : null}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
              gap: 1.2,
            }}
          >
            {visibleColumnGroups.map((item) => (
              <ColumnGroup
                key={item.key}
                item={item}
                onAction={handleReviewApproval}
                onReject={setReviewItemPendingRejection}
                onView={setReviewItemViewingDetails}
                onViewAll={openReviewItemsDialog}
                onStop={stopInProgressActivity}
                onOpenCompany={openCompletedCompany}
                onOpenCandidate={openCompletedCandidate}
                onLoadMore={loadMoreWorkspaceActivities}
                hasMore={workspaceActivitiesHasMore}
                loadError={workspaceActivitiesError}
                loadingMore={workspaceActivitiesLoadingMore}
              />
            ))}
          </Box>
        </Stack>

        <Stack
          spacing={1.25}
          sx={{
            gridColumn: { xs: "1", lg: "1 / -1", xl: "auto" },
          }}
        >
          {false ? <Box sx={{ ...panelSx, p: 1.55 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
              <Typography sx={{ fontSize: 21, fontWeight: 800, color: TEXT }}>
                Today&apos;s Agenda
              </Typography>
              <CalendarMonthRoundedIcon sx={{ color: MUTED, fontSize: 20 }} />
            </Stack>
            <Stack spacing={1.2}>
              {agendaItems.map((item) => (
                <Stack key={`${item.time}-${item.title}`} direction="row" spacing={1.2} alignItems="flex-start">
                  <Stack alignItems="center" sx={{ pt: 0.25 }}>
                    <CircleRoundedIcon sx={{ fontSize: 12, color: "#4F46E5" }} />
                    <Box sx={{ width: 2, flex: 1, bgcolor: "#D7DBFF", minHeight: 34, mt: 0.3 }} />
                  </Stack>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT }}>
                      {item.time}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: TEXT, mt: 0.2 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.1 }}>
                      {item.subtitle}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
            <Button
              sx={{
                mt: 1.3,
                textTransform: "none",
                color: BLUE,
                fontWeight: 700,
                px: 0,
              }}
            >
              + 12 more events
            </Button>
          </Box> : null}

          {false ? <Box sx={{ ...panelSx, p: 0, overflow: "hidden" }}>
            <Stack direction="row" sx={{ borderBottom: `1px solid ${BORDER}` }}>
              {[
                { key: "chat", label: "Chat" },
                { key: "quick", label: "Quick Actions" },
              ].map((tab) => (
                <Button
                  key={tab.key}
                  onClick={() => setAssistantTab(tab.key)}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: "none",
                    fontWeight: 700,
                    color: assistantTab === tab.key ? PURPLE : MUTED,
                    borderBottom: assistantTab === tab.key ? `2px solid ${PURPLE}` : "2px solid transparent",
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Stack>

            {assistantTab === "chat" ? (
              <Box sx={{ p: 1.45 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: TEXT }}>
                      Chat with Lead Agent
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>
                      Python LA Search
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label="Running"
                    sx={{
                      bgcolor: toneSoft("success"),
                      color: toneColor("success"),
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                <Stack spacing={1} sx={{ mb: 1.25 }}>
                  {conversationMessages.map((message, index) => {
                    const isUser = message.role === "user";
                    return (
                      <Stack
                        key={`${message.author}-${message.time}-${index}`}
                        direction={isUser ? "row-reverse" : "row"}
                        spacing={1}
                        alignItems="flex-start"
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: isUser ? alpha(PURPLE, 0.16) : alpha(BLUE, 0.12),
                            color: isUser ? PURPLE : BLUE,
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          {isUser ? "RS" : "AI"}
                        </Avatar>
                        <Box
                          sx={{
                            maxWidth: "85%",
                            px: 1.2,
                            py: 1,
                            borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                            bgcolor: isUser ? PURPLE : "#F5F7FF",
                            color: isUser ? "#FFFFFF" : TEXT,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              mb: 0.35,
                              color: isUser ? alpha("#FFFFFF", 0.8) : MUTED,
                            }}
                          >
                            {message.author} - {message.time}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, lineHeight: 1.5 }}>
                            {message.text}
                          </Typography>
                          {message.cta ? (
                            <Button
                              sx={{
                                mt: 0.85,
                                borderRadius: "10px",
                                px: 1.2,
                                py: 0.7,
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: 11.5,
                                color: BLUE,
                                bgcolor: "#FFFFFF",
                                "&:hover": { bgcolor: "#EEF2FF" },
                              }}
                            >
                              {message.cta}
                            </Button>
                          ) : null}
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>

                <Box
                  sx={{
                    borderRadius: "16px",
                    border: `1px solid ${BORDER}`,
                    bgcolor: "#FBFCFF",
                    p: 1,
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Ask me anything..."
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        fontSize: 13,
                        color: TEXT,
                      },
                    }}
                  />
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.85 }}>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" sx={{ color: MUTED }}>
                        <AttachFileRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ color: MUTED }}>
                        <SentimentSatisfiedAltRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <IconButton
                      sx={{
                        width: 34,
                        height: 34,
                        bgcolor: PURPLE,
                        color: "#FFFFFF",
                        "&:hover": { bgcolor: "#4F46E5" },
                      }}
                    >
                      <SendRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </Box>
            ) : (
              <Stack spacing={1} sx={{ p: 1.45 }}>
                {[
                  "Create follow-up reminders",
                  "Open candidate approvals",
                  "Launch a sourcing agent",
                  "Review blocked workflows",
                ].map((item) => (
                  <Button
                    key={item}
                    startIcon={<AutoAwesomeRoundedIcon />}
                    sx={{
                      justifyContent: "flex-start",
                      borderRadius: "14px",
                      border: `1px solid ${BORDER}`,
                      px: 1.5,
                      py: 1.15,
                      textTransform: "none",
                      fontWeight: 600,
                      color: TEXT,
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    {item}
                  </Button>
                ))}
              </Stack>
            )}
          </Box> : null}
        </Stack>
      </Box>

      <Box
        sx={{
          ...panelSx,
          px: 1.6,
          py: 1.2,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1.4} flexWrap="wrap" useFlexGap>
          <LegendItem color="#2563EB" label="Activity Sources:" description="Human (Manual)" />
          <LegendItem color="#8B5CF6" label="" description="Agent (AI)" />
          <LegendItem color="#94A3B8" label="" description="System (Automated)" />
        </Stack>
        <Stack direction="row" spacing={1.4} flexWrap="wrap" useFlexGap>
          <LegendItem color="#DC2626" label="Priorities:" description="Critical" />
          <LegendItem color="#F97316" label="" description="High" />
          <LegendItem color="#F59E0B" label="" description="Medium" />
          <LegendItem color="#16A34A" label="" description="Low" />
        </Stack>
      </Box>

      <Dialog
        open={newActivityOpen}
        onClose={() => !newActivitySaving && setNewActivityOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle data-popup-header sx={{ fontWeight: 700 }}>New Activity</DialogTitle>
        <DialogContent>
          <Stack spacing={1.4} sx={{ pt: 0.75 }}>
            <Select
              size="small"
              value={newActivityForm.entityType}
              onChange={(event) => setNewActivityForm((current) => ({ ...current, entityType: event.target.value }))}
            >
              <MenuItem value="company">Company</MenuItem>
              <MenuItem value="candidate">Candidate</MenuItem>
              <MenuItem value="lead">Lead</MenuItem>
              <MenuItem value="job">Job</MenuItem>
            </Select>
            <TextField label="Entity ID" size="small" type="number" value={newActivityForm.entityId} onChange={(event) => setNewActivityForm((current) => ({ ...current, entityId: event.target.value }))} />
            <TextField label="Activity title" size="small" value={newActivityForm.title} onChange={(event) => setNewActivityForm((current) => ({ ...current, title: event.target.value }))} />
            <TextField label="Details" size="small" multiline minRows={3} value={newActivityForm.details} onChange={(event) => setNewActivityForm((current) => ({ ...current, details: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewActivityOpen(false)} disabled={newActivitySaving}>Cancel</Button>
          <Button variant="contained" onClick={createActivity} disabled={newActivitySaving}>{newActivitySaving ? "Saving…" : "Create"}</Button>
        </DialogActions>
      </Dialog>

      <AIAgentDialog
        open={agentDialogOpen}
        onClose={() => setAgentDialogOpen(false)}
        user={storedUser}
        initialPrompt={agentPrompt}
      />

      <Dialog
        open={Boolean(reviewItemsDialogType)}
        onClose={() => !addingReviewItemsToAgent && !bulkRejectingReviewItems && setReviewItemsDialogType("")}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            // Match the dialog to the table's column footprint, but never
            // exceed the available viewport.
            width: "min(1280px, calc(100vw - 48px))",
            maxWidth: "calc(100vw - 48px)",
            maxHeight: "calc(100dvh - 24px)",
            borderRadius: "18px",
            "@media (max-width:600px)": {
              width: "calc(100vw - 24px)",
              maxWidth: "calc(100vw - 24px)",
            },
          },
        }}
      >
        <DialogTitle data-popup-header sx={{ fontWeight: 700, pr: 7 }}>
          {visibleColumnGroups.find((group) => group.key === itemsDialogGroup)?.label || "Activities"}
          <IconButton data-popup-close
            aria-label="Close activity items"
            onClick={() => setReviewItemsDialogType("")}
            disabled={addingReviewItemsToAgent || bulkRejectingReviewItems}
            sx={{
              position: "absolute",
              top: 14,
              right: 16,
              width: 32,
              height: 32,
              border: `1px solid ${BORDER}`,
              borderRadius: "50%",
              color: MUTED,
              "&:hover": { backgroundColor: "#F8FAFC", color: TEXT },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${BORDER}` }}
          >
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              {isReviewItemsDialog
                ? "Review records directly, or select records for a bulk action."
                : "View records from this activity column."}
            </Typography>
            <Select
              size="small"
              value={reviewItemsDialogType}
              onChange={(event) => {
                setReviewItemsDialogType(event.target.value);
                setSelectedReviewItemIds([]);
              }}
              disabled={addingReviewItemsToAgent || bulkRejectingReviewItems}
              sx={{ minWidth: 160, fontSize: 13 }}
            >
              <MenuItem value="all">All record types</MenuItem>
              <MenuItem value="company">Companies</MenuItem>
              <MenuItem value="candidate">Candidates</MenuItem>
              <MenuItem value="job">Jobs</MenuItem>
              <MenuItem value="lead">Leads</MenuItem>
              <MenuItem value="contact">Contacts</MenuItem>
            </Select>
          </Stack>
          {workspaceActivitiesError ? (
            <Box role="alert" sx={{ px: 2.5, py: 1.5 }}>
              <Typography color="error">{workspaceActivitiesError}</Typography>
              <Button onClick={() => setWorkspaceActivitiesError("")}>Retry loading records</Button>
            </Box>
          ) : workspaceActivitiesHasMore ? (
            <Box role="status" sx={{ px: 2.5, py: 1.5 }}>
              <Typography sx={{ fontSize: 13, color: MUTED }}>Loading all records. The result count will update when loading finishes.</Typography>
              <LinearProgress sx={{ mt: 1 }} />
            </Box>
          ) : null}
          <GenericDataTable
            resetPageSignal={`${itemsDialogGroup}:${reviewItemsDialogType}`}
            columns={[
              ...(reviewItemsDialogType === "candidate"
                ? [{ id: "candidateName", label: "Name", minWidth: 180, sortable: true }]
                : []),
              { id: "name", label: reviewItemsDialogType === "contact" ? "Name" : "Title", minWidth: 220, sortable: true },
              { id: "companyName", label: "Company Name", minWidth: 160, sortable: true },
              ...(!["lead", "contact"].includes(reviewItemsDialogType)
                ? [{ id: "detail", label: "Details", minWidth: 180, sortable: true }]
                : []),
              { id: "source", label: "Source", minWidth: 160, sortable: true },
              { id: "owner", label: "Owner", minWidth: 130, sortable: true },
              { id: "createdAt", label: "Created", minWidth: 170, sortable: true },
              { id: "actions", label: "Actions", minWidth: 185, sortable: false },
            ]}
            rows={reviewItemsForDialog.map((item) => ({
              ...item,
              candidateName: String(item.candidate?.name || item.metadata?.candidate_name || "").trim(),
              name: item.reviewEntityType === "contact"
                ? String(
                    item.contactName ||
                    item.metadata?.proposed_contact?.name ||
                    item.metadata?.proposed_contact?.full_name ||
                    item.metadata?.proposed_contact?.contact_name ||
                    (/^Review contact:\s*/i.test(item.title || "")
                      ? item.title.replace(/^Review contact:\s*/i, "") : "") ||
                    "Unknown contact"
                  ).trim()
                : item.reviewEntityType === "lead" ? item.leadTitle || "Untitled lead" : item.title,
              companyName: item.company?.name || "—",
              source: getReviewSourceLabel(item.metadata, item.metadata?.proposed_job || item.metadata?.proposed_contact),
              jobLink: item.reviewEntityType === "lead" ? getReviewJobLink(item.metadata) : "",
              jobLocation: String(
                item.metadata?.proposed_job?.location ||
                  item.metadata?.proposed_job?.job_location ||
                  ""
              ).trim(),
              owner: item.actorName || "AI / System",
              createdAtDisplay: formatActivityDateTime(item.createdAt),
            }))}
            includeSelectionColumn={isReviewItemsDialog}
            selectedIds={selectedReviewItemIds}
            onSelectedIdsChange={(ids) => setSelectedReviewItemIds((ids || []).map(String))}
            renderRow={(row) => (
              <>
                {reviewItemsDialogType === "candidate" && (
                  <TableCell sx={{ minWidth: 180, py: 1.25 }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ width: 30, height: 30, bgcolor: "#BDBDBD", color: "#FFFFFF", fontSize: 20 }}>
                        {row.candidateName.charAt(0).toUpperCase() || "—"}
                      </Avatar>
                      <Typography sx={{ fontSize: 14, color: "#1E4D92", textTransform: "uppercase" }}>
                        {row.candidateName || "—"}
                      </Typography>
                    </Stack>
                  </TableCell>
                )}
                <TableCell sx={{ minWidth: 220, py: 1.25 }}>
                  {row.reviewEntityType === "contact" ? (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ width: 30, height: 30, bgcolor: "#9C4FFF", color: "#FFFFFF", fontSize: 20 }}>
                        {row.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, color: "#1E4D92" }}>{row.name}</Typography>
                        <Typography sx={{ fontSize: 12, color: SUBTLE }}>{row.contactTitle || "—"}</Typography>
                        <Typography sx={{ fontSize: 12, color: MUTED }}>{row.title}</Typography>
                      </Box>
                    </Stack>
                  ) : row.reviewEntityType === "lead" ? (
                    <Box>
                      <Typography sx={{ fontSize: 14, color: "#1E4D92" }}>{row.name}</Typography>
                      {row.leadActivityDescription && row.leadActivityDescription !== row.name && (
                        <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>{row.leadActivityDescription}</Typography>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      {row.reviewEntityType === "job" && row.jobTitle && (
                        <Typography sx={{ fontSize: 14, color: "#1E4D92", mb: 0.25 }}>{row.jobTitle}</Typography>
                      )}
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: row.reviewEntityType === "job" && row.jobTitle ? MUTED : TEXT }}>{row.name}</Typography>
                    </Box>
                  )}
                  {row.jobLink ? (
                    <Typography
                      component="a"
                      href={row.jobLink}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      sx={{
                        display: "inline-block",
                        mt: 0.35,
                        fontSize: 12,
                        color: BLUE,
                        fontWeight: 600,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {row.jobLocation
                        ? `Open job posting · ${row.jobLocation}`
                        : "Open job posting"}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell sx={{ minWidth: 160, py: 1.25 }}><CompanyTableCell row={row} website={row.companyLink?.website || row.company?.website} onClick={row.companyLink?.existingId ? () => openCompletedCompany(row.company) : undefined} href={!row.companyLink?.existingId ? row.companyLink?.website : undefined}>{row.companyLink?.existingId ? <Typography sx={{ fontSize: 11.5, color: "#067647", fontWeight: 700 }}>Already exists</Typography> : row.companyLink?.website ? <Typography sx={{ fontSize: 11.5, color: MUTED }}>Live website</Typography> : null}</CompanyTableCell></TableCell>
                {!["lead", "contact"].includes(reviewItemsDialogType) && (
                  <TableCell sx={{ minWidth: 180, py: 1.25 }}>
                    <Typography sx={{ fontSize: 13, color: MUTED }}>{row.detail || "—"}</Typography>
                  </TableCell>
                )}
                <TableCell sx={{ minWidth: 160, py: 1.25 }}>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>{row.source}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 130, py: 1.25 }}>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>{row.owner}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 170, py: 1.25 }}>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>{row.createdAtDisplay}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 205, py: 1.25, whiteSpace: "nowrap" }}>
                  {isReviewItemsDialog ? (
                  <Stack direction="row" spacing={0.75} flexWrap="nowrap">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleReviewApproval(row, "approve")}
                      disabled={addingReviewItemsToAgent || bulkRejectingReviewItems}
                      sx={{ minWidth: 72, px: 1.25, whiteSpace: "nowrap", textTransform: "none", fontSize: 12, fontWeight: 700 }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setReviewItemPendingRejection(row)}
                      disabled={addingReviewItemsToAgent || bulkRejectingReviewItems}
                      sx={{ minWidth: 62, px: 1.25, whiteSpace: "nowrap", textTransform: "none", fontSize: 12, fontWeight: 700 }}
                    >
                      Reject
                    </Button>
                  </Stack>
                  ) : (
                    <Button size="small" onClick={() => setReviewItemViewingDetails(row)}>
                      View
                    </Button>
                  )}
                </TableCell>
              </>
            )}
            defaultOrderBy="createdAt"
            defaultOrderDirection="desc"
            initialRowsPerPage={10}
            visualVariant="reference"
          />
        </DialogContent>
        <DialogActions data-popup-footer
          sx={{
            px: 3,
            py: 2,
            "@media (max-width:600px)": {
              px: 1.5,
              py: 1,
              flexDirection: "column-reverse",
              alignItems: "stretch",
              "& .MuiButton-root": { width: "100%" },
            },
          }}
        >
          <Button onClick={() => setReviewItemsDialogType("")} disabled={addingReviewItemsToAgent || bulkRejectingReviewItems} sx={{ textTransform: "none" }}>
            {isReviewItemsDialog ? "Cancel" : "Close"}
          </Button>
          {isReviewItemsDialog && <>
          <Button
            variant="outlined"
            color="error"
            onClick={bulkRejectSelectedReviewItems}
            disabled={!selectedReviewItemIds.length || addingReviewItemsToAgent || bulkRejectingReviewItems}
            sx={{ whiteSpace: "nowrap", textTransform: "none", fontWeight: 700 }}
          >
            {bulkRejectingReviewItems
              ? "Rejecting…"
              : `Bulk Reject (${selectedReviewItemIds.length})`}
          </Button>
          <Button
            variant="contained"
            onClick={addSelectedReviewItemsToAgent}
            disabled={!selectedReviewItemIds.length || addingReviewItemsToAgent || bulkRejectingReviewItems}
            sx={{ whiteSpace: "nowrap", textTransform: "none", fontWeight: 700 }}
          >
            {addingReviewItemsToAgent ? "Adding to AI Agent…" : `Add selected to AI Agent (${selectedReviewItemIds.length})`}
          </Button>
          </>}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(reviewItemViewingDetails)}
        onClose={() => setReviewItemViewingDetails(null)}
        aria-labelledby="review-item-details-dialog-title"
        PaperProps={{ sx: { borderRadius: "18px", width: "100%", maxWidth: 620 } }}
      >
        <DialogTitle data-popup-header id="review-item-details-dialog-title" sx={{ fontWeight: 700 }}>
          <Typography data-popup-title
            component={getReviewJobLink(reviewItemViewingDetails?.metadata) ? "a" : "span"}
            href={getReviewJobLink(reviewItemViewingDetails?.metadata) || undefined}
            target={getReviewJobLink(reviewItemViewingDetails?.metadata) ? "_blank" : undefined}
            rel={getReviewJobLink(reviewItemViewingDetails?.metadata) ? "noopener noreferrer" : undefined}
            sx={{
              font: "inherit",
              color: getReviewJobLink(reviewItemViewingDetails?.metadata) ? BLUE : "inherit",
              textDecoration: "none",
              "&:hover": getReviewJobLink(reviewItemViewingDetails?.metadata) ? { textDecoration: "underline" } : undefined,
            }}
          >
            {reviewItemViewingDetails?.title || "Review details"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const metadata = reviewItemViewingDetails?.metadata || {};
            const proposal = metadata.proposed_job || metadata.proposed_contact || {};
            const fields = [
              ["Title", proposal.job_title || proposal.title || proposal.name || proposal.full_name],
              ["Company", proposal.company_name || proposal.company || proposal.employer_name],
              ["Location", proposal.location || proposal.job_location || proposal.city],
              ["Employment type", proposal.employment_type || proposal.job_type],
              ["Description", proposal.description || proposal.job_description || proposal.summary],
              ["Source", proposal.source || proposal.source_name],
              ["Email", proposal.email],
              ["Phone", proposal.phone],
              ["LinkedIn", proposal.linkedin_url || proposal.linkedin],
            ].filter(([, value]) => String(value || "").trim());

            return fields.length ? (
              <Stack spacing={1.25}>
                {fields.map(([label, value]) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 700 }}>
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        mb: 0,
                        fontSize: 13.5,
                        color: TEXT,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <DialogContentText>No additional details are available for this item.</DialogContentText>
            );
          })()}
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setReviewItemViewingDetails(null)} sx={{ textTransform: "none", fontWeight: 700 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(primaryCompanySelection)}
        onClose={() => setPrimaryCompanySelection(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle data-popup-header sx={{ fontWeight: 700 }}>Select the primary company</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            The CV includes multiple companies. Choose the one the AI Agent should research for Contacts and Leads. The other extracted companies will not enter that research flow.
          </DialogContentText>
          <RadioGroup
            value={selectedPrimaryCompanyName}
            onChange={(event) => setSelectedPrimaryCompanyName(event.target.value)}
          >
            {getPrimaryCompanyOptions(primaryCompanySelection).map((companyName) => (
              <FormControlLabel
                key={companyName}
                value={companyName}
                control={<Radio />}
                label={companyName}
                sx={{ mx: 0, borderBottom: `1px solid ${BORDER}`, py: 0.4 }}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPrimaryCompanySelection(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selectedPrimaryCompanyName}
            onClick={async () => {
              if (!primaryCompanySelection || !selectedPrimaryCompanyName) return;
              try {
                await updateReviewActivity(primaryCompanySelection, "approve", {
                  primaryCompanyName: selectedPrimaryCompanyName,
                });
                setPrimaryCompanySelection(null);
                setSelectedPrimaryCompanyName("");
              } catch (error) {
                console.error("Failed to approve the selected primary company:", error);
              }
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Approve & Start Research
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(reviewItemPendingRejection)}
        onClose={() => setReviewItemPendingRejection(null)}
        aria-labelledby="reject-review-item-dialog-title"
        aria-describedby="reject-review-item-dialog-description"
        PaperProps={{ sx: { borderRadius: "18px", minWidth: { sm: 420 } } }}
      >
        <DialogTitle data-popup-header id="reject-review-item-dialog-title" sx={{ fontWeight: 700 }}>
          Reject activity?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reject-review-item-dialog-description">
            {reviewItemPendingRejection
              ? `Are you sure you want to reject “${reviewItemPendingRejection.title}”? It will be discarded from Needs Review.`
              : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReviewItemPendingRejection(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            className="pill pill-danger"
            onClick={confirmReviewItemRejection}
            sx={[pillButtonInteractiveSx, {
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 400,
              background: `${RED} !important`,
              border: `1px solid ${RED}`,
              boxShadow: "none",
              "&:hover": {
                background: "#FFFFFF !important",
                color: `${RED} !important`,
                borderColor: `${RED} !important`,
                boxShadow: "none",
              },
            }]}
          >
            Reject & Discard
          </Button>
        </DialogActions>
      </Dialog>
      </Box>}
    />
  );
}

export default Activities;
